import { showWatchlistDraftGuidance } from '../../utils/watchlistDraftGuidance';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ImportFailureNotice from '../shared/ImportFailureNotice';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    applyWatchlistRevision,
    applyWatchlistContextRefreshRevision,
    confirmWatchlistContextRemovals,
    discardWatchlistRevision,
    downloadWatchlistRevisionExport,
    fetchLatestWatchlistRevisions,
    fetchWatchlistRevision,
    fetchWatchlistRevisionSnapshot,
    fetchWatchlistRevisionVersion,
    fetchWatchlistRevisions,
    ignoreWatchlistRevision,
    rejectWatchlistContextRemovals,
    stageWatchlistRestore,
    stageWatchlistRestoreRows,
} from '../../utils/ApiCall';
import { parseWatchlistPublicationReceipt } from '../../utils/watchlistPublication';
import {
    normalizeWatchlistLifecycle,
    watchlistLifecycleProgressStatus,
} from '../../utils/watchlistLifecycle';
import {
    isNothingMatched,
    NOTHING_MATCHED_OUTCOME,
    nothingMatchedMessage,
} from '../../utils/watchlistImportOutcome';
import { uppercaseLabel } from '../../styles/typography';
import { CountryChip } from '../shared/securityDisplayPrimitives';
import {
    HistoryCategoryChip,
    HistoryStatusIcon,
    HistorySummaryMetrics,
    historyLedgerRowSx,
} from '../shared/HistoryLedgerPrimitives';
import ImportDiffDetails from '../ImportDiffDetails';
import WatchlistCompletedResultDialog from './WatchlistCompletedResultDialog';
import useWatchlistReviewIdentity from './useWatchlistReviewIdentity';
import SkippedUploadRowsTable, { notConsideredRows, notConsideredText } from './SkippedUploadRows';
import Skeleton from '../ui/Skeleton';
import { createSkeletonWidths } from '../data_table/skeletonWidths';
import WatchlistContextDetails from './WatchlistContextDetails';
import { hasFactorDataUpdate } from '../../context/factorDataSyncPolicy';
import { formatUserFactorModel } from '../../utils/factorDataOptions';
import { announceContextRefreshApproval } from '../../utils/watchlistContextRefreshApproval';
import {
    duplicateSecurityReasonText,
    isDuplicateSecurityRow,
} from '../../utils/watchlistDuplicateSecurity';
import {
    canonicalWatchlistPublicationObservation,
    isSecurityOperationEventForScope,
    parseWatchlistDeltaClassification,
    scopeSecurityOperationEventDetail,
    USER_SETTINGS_OPEN_EVENT,
    WATCHLIST_DELTA_CLASSIFICATIONS,
    WATCHLIST_REVISION_CLOSED_EVENT,
    WATCHLIST_REVISION_PROGRESS_EVENT,
    WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT,
} from '../../utils/securityResolutionEvents';
import { useSecurityOperationContext } from '../../context/SecurityOperationContext';
import {
    APPLICATION_CONTEXT_LABELS,
    watchlistContextUpdateLabels,
} from '../../context/applicationContextPresentation';
import {
    formatWatchlistUpdateTime,
    watchlistDisplayVersion,
    watchlistVersionLabel,
} from '../../utils/watchlistVersionLabels';
import {
    watchlistPanelSx,
    watchlistSemanticFrameSx,
    watchlistTableFrameSx,
} from '../../styles/watchlistMsiStyles';

const SECURITY_RESOLUTION_UNASSIGNED_FOCUS_TARGET = 'unassigned_fields';
const RESTORE_NOTICE_VISIBLE_MS = 7000;
// An import that matched nothing ended without a version: not a failure.
const NOTHING_ADDED_LABEL = 'Nothing added';

function openSecurityResolutionUnassignedFields() {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.dispatchEvent(new CustomEvent(USER_SETTINGS_OPEN_EVENT, {
            detail: {
                tab: 'msi',
                focusTarget: SECURITY_RESOLUTION_UNASSIGNED_FOCUS_TARGET,
            },
        }));
    } catch (_error) {
        // CustomEvent is unavailable in some minimal test environments.
    }
}

const REVISION_PAGE_LIMIT = 10;
const SNAPSHOT_PAGE_LIMIT = 100;
const MAX_APPLIED_REVISION_REFRESH_KEYS = 64;

function restoreApplyAttemptId(revisionId) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    return normalizedRevisionId
        ? `watchlist-restore-apply:${normalizedRevisionId}`
        : '';
}

const SOURCE_CONFIG = {
    upload: {
        label: 'Upload',
        tooltip: 'Watchlist changed by upload',
        color: 'primary',
    },
    security_resolution: {
        label: 'Security Resolution',
        tooltip: 'Watchlist changed by Security Resolution',
        color: 'secondary',
    },
    msi_reconciliation: {
        label: 'Security-master update',
        tooltip: 'Removed by security-master update',
        color: 'warning',
    },
    restore: {
        label: 'Restore',
        tooltip: 'Watchlist changed by restore',
        color: 'success',
    },
};

const DIFF_BUCKETS = [
    { key: 'added', label: 'Added', color: 'success', borderColor: 'success.light' },
    { key: 'removed', label: 'Removed', color: 'error', borderColor: 'error.light' },
    { key: 'attributes_changed', label: 'Details updated', color: 'warning', borderColor: 'warning.light' },
    { key: 'unresolved_excluded', label: 'Ignored', color: 'warning', borderColor: 'warning.light' },
    { key: 'inactive_excluded', label: 'Needs attention', color: 'warning', borderColor: 'warning.light' },
    { key: 'restored_from_history', label: 'Restored', color: 'success', borderColor: 'success.light' },
];

const DETAIL_COUNT_CHIP_CONFIG = [
    { key: 'added', label: 'Added', color: 'success' },
    { key: 'removed', label: 'Removed', color: 'error' },
    { key: 'attributes_changed', label: 'Details updated', color: 'warning' },
    { key: 'unresolved_excluded', label: 'Ignored', color: 'warning' },
    { key: 'inactive_excluded', label: 'Needs attention', color: 'warning' },
    { key: 'restored_from_history', label: 'Restored', color: 'success' },
];

const EXCLUSION_CATEGORY_LABELS = {
    invalid_input: 'Invalid input',
    unresolved: 'Unresolved',
    ambiguous: 'Needs country review',
    ambiguous_within_watchlist: 'Conflicts with watchlist',
    inactive: 'Inactive',
    duplicate_security: 'Duplicate',
};

const REASON_LABELS = {
    security_delisted: 'Security delisted',
    identifier_expired: 'Identifier expired',
    identifier_value_changed: 'Security record updated',
    selected_country_invalidated: 'Selected country invalidated',
    security_replaced_by_successor: 'Security replaced by successor',
    missing_identifier_value: 'Missing identifier value',
    invalid_identifier_length: 'Invalid identifier length',
    country_hint_not_available: 'Country not available',
    identity_resolution_failed: 'Could not resolve identifier',
};

const FIELD_LABELS = {
    inbound_id_type: 'Identifier type',
    inbound_id_value: 'Identifier value',
    selected_country: 'Country',
    uploaded_country_hint: 'Requested country',
    uploaded_ticker: 'Ticker',
    attributes_json: 'Attributes',
    user_beta_sources: 'Beta sources',
};

const RESOLVER_IDENTIFIER_FIELDS = new Set([
    'resolved_sedol',
    'resolved_cusip',
    'resolved_isin',
]);

const COUNTRY_CHANGE_FIELDS = new Set(['selected_country']);

function countValue(counts, key) {
    const raw = counts?.[key];
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;
}

function getCounts(revision) {
    return revision?.diff_summary_json?.counts || {};
}

function getExcludedCount(counts) {
    return countValue(counts, 'unresolved_excluded') + countValue(counts, 'inactive_excluded');
}

function formatDateTime(value) {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString();
}

// A change that only adds securities: a Build Watchlist additions or grid
// "+ Add security" batch, an identifier looked up on Apply, or a pending
// change those amended. A combined draft records only its last edit, so its
// own diff must show nothing but additions.
const ADDITION_ACTIONS = new Set(['manual_add_batch', 'manual_add_unresolved_identifier']);
const ADDITION_EDIT_KINDS = new Set(['addition', 'identifier_addition']);
const NON_ADDITION_COUNTS = [
    'removed',
    'changed',
    'identifier_changed',
    'country_changed',
    'attributes_changed',
    'restored_from_history',
];

function isAdditionChange(metadata, counts) {
    if (ADDITION_ACTIONS.has(metadata?.action)) return true;
    return metadata?.action === 'combined_draft'
        && metadata?.combined_draft_contract === 'watchlist-combined-draft.v1'
        && ADDITION_EDIT_KINDS.has(metadata?.draft_edit_kind)
        && Boolean(counts)
        && NON_ADDITION_COUNTS.every((key) => !Number(counts[key] || 0));
}

function formatSourceLabel(source, metadata = {}, counts = null) {
    if (isAdditionChange(metadata, counts)) return 'Addition';
    if (metadata?.action === 'market_health_repair') return 'Market mapping repair';
    if (metadata?.action === 'watchlist_context_refresh') {
        return watchlistContextUpdateLabels(
            metadata.context_refresh_changed_fields,
            metadata.context_refresh_available,
        ).entry;
    }
    if (source === 'upload' && metadata?.ingress_kind === 'm365_excel') {
        return 'Microsoft 365 Excel';
    }
    if (metadata?.backfill) {
        return 'Upload';
    }
    if (metadata?.action === 'watchlist_fields_edit') {
        return 'Watchlist edit';
    }
    return SOURCE_CONFIG[source]?.label || source || 'Unknown';
}

function revisionSource(revision) {
    const explicitSource = String(
        revision?.change_source || revision?.source || '',
    ).trim();
    if (explicitSource) return explicitSource;

    const metadata = revision?.source_metadata_json || {};
    if (metadata.event_type === 'watchlist_upload_v2_staged' || metadata.backfill) {
        return 'upload';
    }
    return '';
}

function isContextRefresh(revision) {
    return revision?.source_metadata_json?.action === 'watchlist_context_refresh';
}

function isPublishedRevision(revision) {
    return String(revision?.status || '').trim().toLowerCase() === 'applied';
}

function isCurrentVersion(revision) {
    return Boolean(revision?.is_current_version ?? revision?.is_current);
}

// History entry heading. Only published versions carry a number; a change
// that is pending or was never published may never become a version.
function revisionHeading(revision) {
    const label = watchlistVersionLabel(revision);
    if (label) return label;
    if (isContextRefresh(revision)) return '';
    const status = String(revision?.status || '').trim().toLowerCase();
    if (status === 'pending_apply') return 'New version';
    if (status === 'applied') return 'Published version';
    return 'Unpublished change';
}

// A pending change rebuilt on a newer Watchlist, or edited before approval,
// continues as a successor that names it (draft_predecessor_revision_id). It
// is the same change, so History lists only the successor; the server keeps
// both.
function withoutReplacedDrafts(revisions) {
    const replaced = new Set(revisions
        .map((revision) => String(revision?.source_metadata_json?.draft_predecessor_revision_id || ''))
        .filter(Boolean));
    if (replaced.size === 0) return revisions;
    return revisions.filter((revision) => !(
        String(revision?.status || '').trim().toLowerCase() === 'superseded'
        && replaced.has(String(revision?.id || ''))
    ));
}

function revisionAccessibleName(revision) {
    const heading = revisionHeading(revision);
    if (watchlistDisplayVersion(revision)) return heading;
    const label = heading || formatSourceLabel(
        revisionSource(revision),
        revision?.source_metadata_json || {},
        revision?.diff_summary_json?.counts,
    );
    return `${label} from ${formatDateTime(revision?.created_at)}`;
}

function contextUpdateNoteText(update) {
    const labels = watchlistContextUpdateLabels(update?.changed_fields, update?.factor_context);
    const time = formatWatchlistUpdateTime(update?.applied_at);
    return time ? `${labels.note} ${time}` : labels.note;
}

function factorContextSummary(context) {
    if (!context || typeof context !== 'object') return '';
    const parts = [
        context.vendor ? humanizeToken(context.vendor) : '',
        [context.factor_model, context.factor_horizon].filter(Boolean).join(' '),
        context.factor_as_of_date ? `data as of ${context.factor_as_of_date}` : '',
        context.factor_release_id != null && context.factor_release_id !== ''
            ? `release ${context.factor_release_id}`
            : '',
    ].filter(Boolean);
    return parts.join(' · ');
}

const CONTENT_CHANGE_COUNT_KEYS = [
    'added',
    'removed',
    'identifier_changed',
    'country_changed',
    'attributes_changed',
    'restored_from_history',
];

function formatSourceDescription(revision) {
    const metadata = revision?.source_metadata_json || {};
    if (metadata?.action === 'market_health_repair') {
        return `Review Yahoo symbol ${metadata.market_repair_proposal?.repair_symbol || 'update'} for the same security.`;
    }
    if (metadata?.action === 'watchlist_context_refresh') {
        // Say why a published refresh became a version: it changed securities.
        const counts = revision?.diff_summary_json?.counts || {};
        const changedSecurities = CONTENT_CHANGE_COUNT_KEYS.some(
            (key) => countValue(counts, key) > 0,
        );
        const labels = watchlistContextUpdateLabels(
            metadata.context_refresh_changed_fields,
            metadata.context_refresh_available,
        );
        return watchlistDisplayVersion(revision) && changedSecurities
            ? `Securities changed with this ${labels.phrase ?? labels.entry.toLowerCase()}`
            : null;
    }
    if (revisionSource(revision) === 'upload' && metadata?.ingress_kind === 'm365_excel') {
        return 'Linked Excel worksheet';
    }
    if (metadata?.backfill) {
        return 'Imported from your current watchlist';
    }
    if (metadata?.filename) {
        return `File: ${metadata.filename}`;
    }
    if (metadata?.action === 'watchlist_fields_edit') {
        return 'Watchlist details updated';
    }
    if (revision?.change_source === 'msi_reconciliation') {
        return revision?.msi_snapshot_version
            ? `Security-master snapshot ${revision.msi_snapshot_version}`
            : 'Security-master update';
    }
    return null;
}

function humanizeToken(value) {
    const text = String(value || '').trim();
    if (!text) return '-';
    return text
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatIdentifierType(value) {
    const text = String(value || '').trim();
    if (!text || text.toLowerCase() === 'cognive_id') return '';
    return text.toUpperCase();
}

function formatIdentifier(row = {}) {
    const inboundValue = row?.inbound_id_value == null ? '' : String(row.inbound_id_value).trim();
    if (inboundValue) {
        return {
            type: formatIdentifierType(row?.inbound_id_type),
            value: inboundValue,
        };
    }

    const ticker = row?.uploaded_ticker == null ? '' : String(row.uploaded_ticker).trim();
    if (ticker) {
        return { type: '', value: ticker };
    }

    return { type: '', value: 'Identifier unavailable' };
}

function formatFieldLabel(field) {
    const key = String(field || '').trim();
    return FIELD_LABELS[key] || humanizeToken(key);
}

function displayableChangedFields(fields = []) {
    if (!Array.isArray(fields)) return [];
    return fields.filter((field) => !RESOLVER_IDENTIFIER_FIELDS.has(field));
}

function formatChangedFields(fields = []) {
    const displayFields = displayableChangedFields(fields);
    if (displayFields.length === 0) return '-';
    return displayFields.map(formatFieldLabel).join(', ');
}

function formatDisplayValue(value) {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || '-';
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (Array.isArray(value)) {
        const values = value
            .map(formatDisplayValue)
            .filter((item) => item !== '-');
        return values.length ? values.join(', ') : '-';
    }
    if (typeof value === 'object') {
        const entries = Object.entries(value)
            .map(([key, entryValue]) => {
                const formatted = formatDisplayValue(entryValue);
                return formatted === '-' ? null : `${formatFieldLabel(key)}: ${formatted}`;
            })
            .filter(Boolean);
        return entries.length ? entries.join('; ') : '-';
    }
    return String(value);
}

function formatFieldValue(row = {}, field) {
    if (!row) return '-';
    if (field === 'inbound_id_type') {
        return formatIdentifierType(row?.inbound_id_type) || '-';
    }
    if (field === 'selected_country') {
        return formatDisplayValue(row?.selected_country);
    }
    if (field === 'uploaded_country_hint') {
        return formatDisplayValue(row?.uploaded_country_hint);
    }
    if (field === 'uploaded_ticker') {
        return formatDisplayValue(row?.uploaded_ticker || row?.ticker);
    }
    return formatDisplayValue(row?.[field]);
}

function formatFieldSnapshot(row, fields = [], fallback = '-') {
    const values = displayableChangedFields(fields)
        .map((field) => {
            const value = formatFieldValue(row, field);
            return value === '-' ? null : `${formatFieldLabel(field)}: ${value}`;
        })
        .filter(Boolean);
    return values.length ? values.join('; ') : fallback;
}

function formatTicker(row = {}) {
    return formatDisplayValue(row?.uploaded_ticker || row?.ticker);
}

function formatCountry(row = {}) {
    return formatDisplayValue(row?.selected_country || row?.uploaded_country_hint || row?.country);
}

function formatRequestedCountry(row = {}) {
    return formatDisplayValue(row?.uploaded_country_hint || row?.selected_country || row?.requested_country);
}

function reasonValue(row = {}) {
    const raw = row?.resolution_reason_code
        || row?.reason_code
        || row?.detail_json?.reason_code
        || row?.detail_json?.code
        || row?.detail_json?.reason_detail
        || row?.detail_json?.reason
        || row?.reason;
    return String(raw || '').trim();
}

function hasDisplayReason(row = {}) {
    return reasonValue(row) !== '';
}

function formatReason(row = {}) {
    if (isDuplicateSecurityRow(row)) return duplicateSecurityReasonText(row);
    const raw = reasonValue(row);
    const text = String(raw || '').trim();
    return REASON_LABELS[text] || humanizeToken(text);
}

function formatCategory(row = {}) {
    const raw = row?.exclusion_category || row?.detail_json?.category || row?.resolution_status;
    const text = String(raw || '').trim();
    return EXCLUSION_CATEGORY_LABELS[text] || humanizeToken(text);
}

function asObjectArray(value) {
    if (Array.isArray(value)) {
        return value.filter((item) => item && typeof item === 'object');
    }
    return value && typeof value === 'object' ? [value] : [];
}

function expandBucketEntry(key, value) {
    if (Array.isArray(value)) {
        return value.map((item, index) => ({
            key: `${key}-${index}`,
            group_key: key,
            ...(item && typeof item === 'object' ? item : {}),
        }));
    }
    if (value && typeof value === 'object' && Array.isArray(value.rows)) {
        return value.rows.map((item, index) => ({
            key: `${key}-${index}`,
            group_key: key,
            ...(item && typeof item === 'object' ? item : {}),
        }));
    }
    return [{
        key,
        ...(value && typeof value === 'object' ? value : {}),
    }];
}

function detailRowsForBucket(diffDetails, bucketKey) {
    const rawBucket = diffDetails?.[bucketKey];
    if (!rawBucket) return [];
    if (Array.isArray(rawBucket)) {
        return rawBucket;
    }
    if (typeof rawBucket === 'object') {
        return Object.entries(rawBucket).flatMap(([key, value]) => expandBucketEntry(key, value));
    }
    return [];
}

const TERMINAL_REVISION_LIFECYCLE_STATUSES = new Set([
    'succeeded',
    'unchanged',
    'failed',
    'cancelled',
    'stale',
    'superseded',
]);

function mergeMonotonicLifecycle(current, next) {
    if (!current?.lifecycle || !next?.lifecycle) return next;
    let currentLifecycle;
    let nextLifecycle;
    try {
        currentLifecycle = normalizeWatchlistLifecycle(current.lifecycle);
        nextLifecycle = normalizeWatchlistLifecycle(next.lifecycle);
    } catch (_error) {
        return next;
    }
    if (
        currentLifecycle.watchlist_revision_id
        !== nextLifecycle.watchlist_revision_id
    ) {
        return next;
    }
    const currentTerminal = TERMINAL_REVISION_LIFECYCLE_STATUSES.has(
        currentLifecycle.status,
    );
    const nextTerminal = TERMINAL_REVISION_LIFECYCLE_STATUSES.has(nextLifecycle.status);
    const currentCommitted = current?.ui_commit_complete === true;
    const nextCommitted = next?.ui_commit_complete === true;
    const currentProgress = Number(currentLifecycle.progress?.percent) || 0;
    const nextProgress = Number(nextLifecycle.progress?.percent) || 0;
    if (
        (currentCommitted && !nextCommitted)
        || (currentTerminal && !nextTerminal)
        || (currentTerminal && nextTerminal && currentLifecycle.status !== nextLifecycle.status)
        || (!currentTerminal && !nextTerminal && currentProgress > nextProgress)
    ) {
        return {
            ...next,
            lifecycle: current.lifecycle,
            ui_commit_complete: current.ui_commit_complete,
        };
    }
    return {
        ...next,
        ui_commit_complete: currentCommitted || nextCommitted,
    };
}

// Internal ids of context updates folded into the listed versions. They are
// known to the server even though they are notes rather than entries.
function contextUpdateRevisionIds(items) {
    const ids = new Set();
    (Array.isArray(items) ? items : []).forEach((item) => {
        (Array.isArray(item?.context_updates) ? item.context_updates : []).forEach((update) => {
            const revisionId = String(update?.revision_id || '').trim();
            if (revisionId) ids.add(revisionId);
        });
    });
    return ids;
}

function versionEntryForRevision(items, revisionId) {
    const wanted = String(revisionId || '').trim();
    if (!wanted || !Array.isArray(items)) return null;
    return items.find((item) => String(item?.id || '').trim() === wanted)
        || items.find((item) => (
            Array.isArray(item?.context_updates)
            && item.context_updates.some((update) => (
                String(update?.revision_id || '').trim() === wanted
            ))
        ))
        || null;
}

function mergeRevisionItem(current, next) {
    if (!next?.id) return current;
    const found = current.some((item) => item?.id === next.id);
    if (found) {
        return current.map((item) => (item?.id === next.id ? { ...item, ...next } : item));
    }
    return [next, ...current].sort((left, right) => {
        const leftSeq = Number(left?.revision_seq || 0);
        const rightSeq = Number(right?.revision_seq || 0);
        return rightSeq - leftSeq;
    });
}

// A staged upload that would remove securities held in portfolios waits for
// the Keep-or-Apply choice in its review card. It is not a History entry until
// then; the server's History list leaves it out too.
function awaitsPortfolioDecision(revision) {
    return revision?.status === 'pending_apply'
        && (revision?.decision_status ?? 'pending') === 'pending'
        && Number(revision?.diff_summary_json?.portfolio_impact?.security_count || 0) > 0;
}

function mergeRevisionEventItem(current, next) {
    if (!next?.id) return current;
    const found = current.some((item) => item?.id === next.id);
    if (found) {
        return current.map((item) => {
            if (item?.id !== next.id) return item;
            const merged = mergeMonotonicLifecycle(item, {
                ...item,
                ...next,
                change_source: next.change_source || item.change_source,
                source: next.source || item.source,
                source_metadata_json: (
                    next.source_metadata_json
                    && Object.keys(next.source_metadata_json).length > 0
                ) ? next.source_metadata_json : item.source_metadata_json,
            });
            return {
                ...merged,
                revision_seq: item.revision_seq,
                status: item.status,
                is_current: item.is_current,
                updated_at: item.updated_at,
                // Version numbering and notes are owned by the durable list read.
                display_version: item.display_version,
                is_current_version: item.is_current_version,
                context_updates: item.context_updates,
                context_update_count: item.context_update_count,
            };
        });
    }
    return mergeRevisionItem(current, next);
}

function mergeRevisionDetail(authoritativeRevision, detail) {
    return {
        ...authoritativeRevision,
        ...detail,
    };
}

function revisionDetailFields(detail) {
    return Object.prototype.hasOwnProperty.call(detail || {}, 'diff_details_json')
        ? { diff_details_json: detail.diff_details_json }
        : {};
}

function mergeRevisionProgress(revision, payload) {
    let lifecycle;
    try {
        lifecycle = normalizeWatchlistLifecycle(payload);
    } catch (_error) {
        return revision;
    }
    const revisionId = String(lifecycle.watchlist_revision_id || '').trim();
    if (!revisionId || String(revision?.id || '') !== revisionId) return revision;
    // Progress events never own the active pointer or revision timestamp.
    // Keep only the normalized lifecycle projection until the next durable
    // list read refreshes the revision record.
    return mergeMonotonicLifecycle(revision, {
        ...revision,
        lifecycle,
        ui_commit_complete: (
            revision?.ui_commit_complete === true
            || payload?.ui_commit_complete === true
        ),
    });
}

function IdentifierCell({ row }) {
    const identifier = formatIdentifier(row);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
            {identifier.type && (
                <Chip
                    size="small"
                    variant="outlined"
                    label={identifier.type}
                    sx={{
                        height: 20,
                        fontSize: '0.64rem',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                    }}
                />
            )}
            <Typography
                variant="body2"
                component="span"
                sx={{
                    fontFamily: identifier.value === 'Identifier unavailable' ? 'inherit' : 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 220,
                }}
            >
                {identifier.value}
            </Typography>
        </Box>
    );
}

IdentifierCell.propTypes = {
    row: PropTypes.object,
};

const COUNTRY_CHIP_SX = {
    height: 22,
    maxWidth: 128,
    '& .MuiChip-label': {
        px: 0.65,
        fontSize: '0.65rem',
        fontWeight: 700,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
};

function CountryDisplay({ country }) {
    const text = String(country || '').trim();
    if (!text || text === '-') {
        return (
            <Typography variant="body2" color="text.secondary">
                -
            </Typography>
        );
    }
    return <CountryChip country={text} sx={COUNTRY_CHIP_SX} />;
}

CountryDisplay.propTypes = {
    country: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function SourceLabel({ revision }) {
    const metadata = revision?.source_metadata_json || {};
    const counts = revision?.diff_summary_json?.counts;
    const source = revisionSource(revision);
    const config = isAdditionChange(metadata, counts)
        ? { tooltip: 'Securities added to the Watchlist', color: 'success' }
        : metadata?.action === 'watchlist_context_refresh'
        ? {
            tooltip: 'Watchlist checked against updated security and factor data',
            color: 'warning',
        }
        : source === 'upload' && metadata?.ingress_kind === 'm365_excel'
        ? {
            label: 'Microsoft 365 Excel',
            tooltip: 'Watchlist changes retrieved from the linked Excel worksheet',
            color: 'primary',
        }
        : SOURCE_CONFIG[source] || {
            label: source || 'Unknown',
            color: 'default',
        };
    return (
        <HistoryCategoryChip
            label={formatSourceLabel(source, metadata, counts)}
            tooltip={config.tooltip || ''}
            color={config.color}
        />
    );
}

SourceLabel.propTypes = {
    revision: PropTypes.object,
};

function contextUpdatesOf(revision) {
    const updates = Array.isArray(revision?.context_updates) ? revision.context_updates : [];
    const total = Math.max(Number(revision?.context_update_count) || 0, updates.length);
    return { updates, total };
}

// A context update kept this version's securities, so it is a note on the
// version rather than a version of its own.
function ContextUpdateCaption({ revision }) {
    const { updates, total } = contextUpdatesOf(revision);
    if (!updates.length) return null;
    const earlier = total - 1;
    return (
        <Typography
            variant="caption"
            color="text.secondary"
            data-testid="watchlist-context-update-note"
            sx={{ fontSize: '0.67rem', whiteSpace: 'nowrap' }}
        >
            {contextUpdateNoteText(updates[0])}
            {earlier > 0 ? ` · ${earlier} earlier ${earlier === 1 ? 'update' : 'updates'}` : ''}
        </Typography>
    );
}

ContextUpdateCaption.propTypes = {
    revision: PropTypes.object,
};

function ContextUpdateList({ revision }) {
    const { updates, total } = contextUpdatesOf(revision);
    if (!updates.length) return null;
    const hidden = total - updates.length;
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Data updates</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                These updates kept this version&apos;s securities, so they are not separate versions.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {updates.map((update) => {
                    const summary = factorContextSummary(update?.factor_context);
                    return (
                        <Typography
                            component="li"
                            variant="body2"
                            key={update?.revision_id || update?.applied_at}
                        >
                            {contextUpdateNoteText(update)}
                            {summary ? ` · ${summary}` : ''}
                        </Typography>
                    );
                })}
            </Box>
            {hidden > 0 && (
                <Typography variant="caption" color="text.secondary">
                    {`${hidden} earlier ${hidden === 1 ? 'update' : 'updates'} not shown`}
                </Typography>
            )}
        </Box>
    );
}

ContextUpdateList.propTypes = {
    revision: PropTypes.object,
};

function ActorLabel({ actor }) {
    const isSystem = String(actor || '').toLowerCase() === 'system';
    if (!isSystem) {
        return null;
    }
    return (
        <Typography
            component="span"
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: '0.64rem', whiteSpace: 'nowrap' }}
        >
            System
        </Typography>
    );
}

ActorLabel.propTypes = {
    actor: PropTypes.string,
};

function RevisionStatusIndicator({ label, color = 'text.secondary', loading = false }) {
    const normalized = String(label || '').toLowerCase();
    const kind = loading
        ? 'info'
        : normalized === 'complete'
            ? 'success'
            : normalized === NOTHING_ADDED_LABEL.toLowerCase()
                ? 'notice'
                : ['failed', 'cancelled'].includes(normalized)
                    ? 'error'
                    : normalized.includes('superseded') || normalized.includes('retry')
                        ? 'warning'
                        : 'pending';
    const tooltip = normalized === 'complete'
        ? 'Watchlist update completed successfully'
        : normalized === NOTHING_ADDED_LABEL.toLowerCase()
            ? 'Nothing was added; your Watchlist is unchanged'
            : normalized === 'failed'
                ? 'Watchlist update failed and was not applied'
                : normalized === 'cancelled'
                    ? 'Watchlist update was cancelled'
                    : label;
    return (
        <HistoryStatusIcon
            label={label}
            tooltip={tooltip}
            kind={kind}
            color={color}
            loading={loading}
        />
    );
}

RevisionStatusIndicator.propTypes = {
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    loading: PropTypes.bool,
};

function MsiRevisionProgressStatus({ revision, sharedOperationStatus = null }) {
    const revisionId = String(revision?.id || '').trim();
    const decisionStatus = String(revision?.decision_status || '').trim().toLowerCase();
    const revisionStatus = String(revision?.status || '').trim().toLowerCase();
    let lifecycle = null;
    try {
        lifecycle = normalizeWatchlistLifecycle(revision?.lifecycle);
    } catch (_error) {
        // A matching live operation can still describe a staged row whose
        // durable lifecycle has not reached this list response yet.
    }
    const liveNothingMatched = sharedOperationStatus?.found === true
        && String(sharedOperationStatus?.revisionId || '').trim() === revisionId
        && sharedOperationStatus?.outcome === NOTHING_MATCHED_OUTCOME;
    if (isNothingMatched(lifecycle) || liveNothingMatched) {
        return <RevisionStatusIndicator label={NOTHING_ADDED_LABEL} color="info.main" />;
    }
    if (
        revisionStatus === 'applied'
        || ['succeeded', 'unchanged'].includes(lifecycle?.status)
    ) {
        return <RevisionStatusIndicator label="Complete" color="success.main" />;
    }
    if (revisionStatus === 'failed') {
        return <RevisionStatusIndicator label="Failed" color="error.main" />;
    }
    const sharedRevisionId = String(
        sharedOperationStatus?.revisionId
        || sharedOperationStatus?.watchlist_revision_id
        || '',
    ).trim();
    if (
        sharedOperationStatus?.found === true
        && revisionId
        && sharedRevisionId === revisionId
    ) {
        const sharedTopLevelStatus = String(
            sharedOperationStatus.topLevelStatus || '',
        ).trim().toLowerCase();
        if (sharedTopLevelStatus === 'success') {
            return <RevisionStatusIndicator label="Complete" color="success.main" />;
        }
        if (['failed', 'partial'].includes(sharedTopLevelStatus)) {
            return <RevisionStatusIndicator label="Failed" color="error.main" />;
        }
        if (sharedOperationStatus.phase === 'removal_review') {
            return <RevisionStatusIndicator label="Awaiting review" color="info.main" />;
        }
        const sharedProgress = Math.max(
            0,
            Math.min(99, Math.round(Number(sharedOperationStatus.progressPercent || 0))),
        );
        return (
            <RevisionStatusIndicator
                label={`Updating ${sharedProgress}%`}
                color="info.main"
                loading
            />
        );
    }
    if (!lifecycle) {
        if (decisionStatus === 'pending') {
            return <RevisionStatusIndicator label="Awaiting review" color="info.main" />;
        }
        if (decisionStatus === 'accepted') {
            return <RevisionStatusIndicator label="Queued" color="info.main" />;
        }
        return null;
    }
    if (lifecycle.status === 'pending_review') {
        return <RevisionStatusIndicator label="Awaiting review" color="info.main" />;
    }
    if (lifecycle.status === 'running' && lifecycle.phase === 'removal_review') {
        return <RevisionStatusIndicator label="Awaiting review" color="info.main" />;
    }
    if (['stale', 'superseded'].includes(lifecycle.status)) {
        return <RevisionStatusIndicator label="Superseded" color="warning.main" />;
    }
    if (lifecycle.retry?.classification === 'retry_pending') {
        return <RevisionStatusIndicator label="Retry needed" color="warning.main" />;
    }
    if (lifecycle.status === 'failed') {
        return <RevisionStatusIndicator label="Failed" color="error.main" />;
    }
    if (lifecycle.status === 'cancelled') {
        return <RevisionStatusIndicator label="Cancelled" color="error.main" />;
    }
    if (lifecycle.status === 'queued') {
        return <RevisionStatusIndicator label="Queued" color="info.main" />;
    }
    const progressStatus = watchlistLifecycleProgressStatus(lifecycle);
    if (!progressStatus) return null;
    return (
        <RevisionStatusIndicator
            label={`Updating ${Math.round(progressStatus.progressPercent)}%`}
            color="info.main"
            loading
        />
    );
}

MsiRevisionProgressStatus.propTypes = {
    revision: PropTypes.object,
    sharedOperationStatus: PropTypes.object,
};

function RevisionCountMetrics({ revision }) {
    const counts = getCounts(revision);
    const added = countValue(counts, 'added');
    const removed = countValue(counts, 'removed');
    const changed = countValue(counts, 'attributes_changed') || countValue(counts, 'changed');
    const excluded = getExcludedCount(counts);
    return (
        <HistorySummaryMetrics
            added={added}
            removed={removed}
            changed={changed}
            excluded={excluded}
        />
    );
}

RevisionCountMetrics.propTypes = {
    revision: PropTypes.object,
};

function DetailCountChips({ counts }) {
    return DETAIL_COUNT_CHIP_CONFIG.map(({ key, label, color }) => ({
        key,
        label,
        color,
        count: countValue(counts, key),
    })).filter(({ count }) => count > 0).map(({ key, label, color, count }) => (
        <Chip
            key={key}
            size="small"
            variant="outlined"
            color={color}
            label={`${label} ${count}`}
            sx={{
                height: 22,
                '& .MuiChip-label': {
                    fontSize: '0.62rem',
                    px: 0.7,
                },
            }}
        />
    ));
}

DetailCountChips.propTypes = {
    counts: PropTypes.object,
};

function BucketFrame({ bucket, title, caption, children }) {
    const tone = bucket.color === 'error'
        ? 'danger'
        : bucket.color === 'success'
            ? 'success'
            : 'warning';
    return (
        <Box
            sx={[
                {
                    p: 1.2,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: bucket.borderColor,
                    bgcolor: (theme) => (
                        theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : alpha(theme.palette.action.hover, 0.08)
                    ),
                },
                watchlistSemanticFrameSx(tone),
            ]}
        >
            <Typography variant="subtitle2" sx={{ color: `${bucket.color}.main`, mb: 0.75 }}>
                {title}
            </Typography>
            {caption && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    {caption}
                </Typography>
            )}
            {children}
        </Box>
    );
}

BucketFrame.propTypes = {
    bucket: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired,
    caption: PropTypes.string,
    children: PropTypes.node.isRequired,
};

function sideRowsForEntry(entry, side) {
    const sideRows = asObjectArray(entry?.[side]);
    const rows = sideRows.length ? sideRows : asObjectArray(entry);
    return rows.map((row, rowIndex) => ({
        row,
        entry,
        key: [
            entry?.key || entry?.group_key || 'row',
            row?.cognive_id,
            row?.inbound_id_type,
            row?.inbound_id_value,
            row?.selected_country || row?.uploaded_country_hint,
            rowIndex,
        ].filter((part) => part !== null && part !== undefined && String(part).trim() !== '').join('-'),
    }));
}

function pairRowsForEntry(entry) {
    const beforeRows = asObjectArray(entry?.before);
    const afterRows = asObjectArray(entry?.after);
    const maxRows = Math.max(beforeRows.length, afterRows.length, 1);
    return Array.from({ length: maxRows }, (_, rowIndex) => {
        const before = beforeRows[rowIndex] || null;
        const after = afterRows[rowIndex] || null;
        const displayRow = after || before || entry;
        return {
            before,
            after,
            displayRow,
            entry,
            key: [
                entry?.key || entry?.group_key || 'row',
                displayRow?.cognive_id,
                displayRow?.inbound_id_type,
                displayRow?.inbound_id_value,
                displayRow?.selected_country || displayRow?.uploaded_country_hint,
                rowIndex,
            ].filter((part) => part !== null && part !== undefined && String(part).trim() !== '').join('-'),
        };
    });
}

// Diff rows carry their attribute values by name; each name is one column.
function diffAttributeNames(tableRows) {
    const names = [];
    tableRows.forEach(({ row }) => {
        const attributes = row?.attributes_json;
        if (!attributes || typeof attributes !== 'object') return;
        Object.keys(attributes).forEach((name) => {
            if (!names.includes(name)) names.push(name);
        });
    });
    return names;
}

function ConcreteRowsBucketSection({ bucket, rows, affectedById }) {
    const identity = useWatchlistReviewIdentity();
    const config = {
        added: {
            title: 'Added',
            ariaLabel: 'Added',
            side: 'after',
            caption: null,
        },
        removed: {
            title: 'Removed',
            ariaLabel: 'Removed',
            side: 'before',
            caption: null,
        },
        restored_from_history: {
            title: 'Restored',
            ariaLabel: 'Restored',
            side: 'after',
            caption: 'Restored rows are shown after current security-master resolution.',
        },
    }[bucket.key];
    const tableRows = rows.flatMap((entry) => sideRowsForEntry(entry, config.side));
    const attributeNames = diffAttributeNames(tableRows);
    const showReasonColumn = bucket.key === 'removed' && tableRows.some(({ row }) => hasDisplayReason(row));

    return (
        <BucketFrame bucket={bucket} title={config.title} caption={config.caption}>
            <TableContainer component={Paper} variant="outlined" sx={[{ borderRadius: 1.5 }, watchlistTableFrameSx]}>
                <Table size="small" aria-label={config.ariaLabel}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>{identity.header}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                            {attributeNames.map((name) => (
                                <TableCell key={`attribute-${name}`} sx={{ fontWeight: 700 }}>{name}</TableCell>
                            ))}
                            {showReasonColumn && <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>}
                            {affectedById && <TableCell sx={{ fontWeight: 700 }}>Affected portfolios</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRows.map(({ row, key }, index) => (
                            <TableRow key={`${bucket.key}-${key || index}`}>
                                <TableCell>
                                    {identity.renderIdentity(row)}
                                </TableCell>
                                <TableCell>
                                    <CountryDisplay country={formatCountry(row)} />
                                </TableCell>
                                {attributeNames.map((name) => (
                                    <TableCell key={`attribute-${name}`}>
                                        {formatDisplayValue(row?.attributes_json?.[name])}
                                    </TableCell>
                                ))}
                                {showReasonColumn && <TableCell>{formatReason(row)}</TableCell>}
                                {affectedById && (
                                    <TableCell>{affectedById.get(String(row?.cognive_id || ''))?.join(', ') || 'None'}</TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {identity.selector}
        </BucketFrame>
    );
}

ConcreteRowsBucketSection.propTypes = {
    bucket: PropTypes.object.isRequired,
    rows: PropTypes.array.isRequired,
    affectedById: PropTypes.instanceOf(Map),
};

function ExcludedRowsBucketSection({ bucket, rows }) {
    const identity = useWatchlistReviewIdentity();
    const tableRows = rows.flatMap((entry) => sideRowsForEntry(entry, 'after'));
    return (
        <BucketFrame
            bucket={bucket}
            title={bucket.label}
            caption="Rows excluded from the active watchlist keep their upload identifier, requested country, category, and reason."
        >
            <TableContainer component={Paper} variant="outlined" sx={[{ borderRadius: 1.5 }, watchlistTableFrameSx]}>
                <Table size="small" aria-label={bucket.label}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>{identity.header}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Requested Country</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRows.map(({ row, key }, index) => (
                            <TableRow key={`${bucket.key}-${key || index}`}>
                                <TableCell>
                                    {identity.renderIdentity(row)}
                                </TableCell>
                                <TableCell>
                                    <CountryDisplay country={formatRequestedCountry(row)} />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        label={formatCategory(row)}
                                        sx={{ height: 20, fontSize: '0.62rem' }}
                                    />
                                </TableCell>
                                <TableCell>{formatReason(row)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {identity.selector}
        </BucketFrame>
    );
}

ExcludedRowsBucketSection.propTypes = {
    bucket: PropTypes.object.isRequired,
    rows: PropTypes.array.isRequired,
};

function CountryChangedBucketSection({ bucket, rows }) {
    const tableRows = rows.flatMap(pairRowsForEntry);
    return (
        <BucketFrame
            bucket={bucket}
            title="Country listing updates"
            caption="Country is the selected listing stored for the watchlist row."
        >
            <TableContainer component={Paper} variant="outlined" sx={[{ borderRadius: 1.5 }, watchlistTableFrameSx]}>
                <Table size="small" aria-label="Country listing updates">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Identifier</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Before Country</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>After Country</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Other Updates</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRows.map(({ before, after, displayRow, entry, key }, index) => {
                            const changedFields = Array.isArray(entry?.changed_fields) ? entry.changed_fields : [];
                            const otherFields = changedFields.filter((field) => !COUNTRY_CHANGE_FIELDS.has(field));
                            return (
                                <TableRow key={`${bucket.key}-${key || index}`}>
                                    <TableCell>
                                        <IdentifierCell row={displayRow} />
                                    </TableCell>
                                    <TableCell>{formatTicker(displayRow)}</TableCell>
                                    <TableCell>
                                        <CountryDisplay country={formatDisplayValue(before?.selected_country)} />
                                    </TableCell>
                                    <TableCell>
                                        <CountryDisplay country={formatDisplayValue(after?.selected_country)} />
                                    </TableCell>
                                    <TableCell>{formatChangedFields(otherFields)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </BucketFrame>
    );
}

CountryChangedBucketSection.propTypes = {
    bucket: PropTypes.object.isRequired,
    rows: PropTypes.array.isRequired,
};

function AttributesChangedBucketSection({ bucket, rows }) {
    const identity = useWatchlistReviewIdentity();
    const tableRows = rows.flatMap(pairRowsForEntry);
    return (
        <BucketFrame
            bucket={bucket}
            title="Details updated"
            caption="Only stored watchlist attributes are shown here; editing those attributes is handled outside history."
        >
            <TableContainer component={Paper} variant="outlined" sx={[{ borderRadius: 1.5 }, watchlistTableFrameSx]}>
                <Table size="small" aria-label="Details updated">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>{identity.header}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Updated Fields</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Before</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>After</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRows.map(({ before, after, displayRow, entry, key }, index) => {
                            const changedFields = Array.isArray(entry?.changed_fields) ? entry.changed_fields : [];
                            return (
                                <TableRow key={`${bucket.key}-${key || index}`}>
                                    <TableCell>
                                        {identity.renderIdentity(displayRow)}
                                    </TableCell>
                                    <TableCell>
                                        <CountryDisplay country={formatCountry(displayRow)} />
                                    </TableCell>
                                    <TableCell>{formatChangedFields(changedFields)}</TableCell>
                                    <TableCell>{formatFieldSnapshot(before, changedFields)}</TableCell>
                                    <TableCell>{formatFieldSnapshot(after, changedFields)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {identity.selector}
        </BucketFrame>
    );
}

AttributesChangedBucketSection.propTypes = {
    bucket: PropTypes.object.isRequired,
    rows: PropTypes.array.isRequired,
};

function DiffBucketSection({ bucket, rows }) {
    if (!rows.length) return null;
    if (bucket.key === 'added' || bucket.key === 'removed' || bucket.key === 'restored_from_history') {
        return <ConcreteRowsBucketSection bucket={bucket} rows={rows} />;
    }
    if (bucket.key === 'unresolved_excluded' || bucket.key === 'inactive_excluded') {
        return <ExcludedRowsBucketSection bucket={bucket} rows={rows} />;
    }
    if (bucket.key === 'country_changed') {
        return <CountryChangedBucketSection bucket={bucket} rows={rows} />;
    }
    return <AttributesChangedBucketSection bucket={bucket} rows={rows} />;
}

DiffBucketSection.propTypes = {
    bucket: PropTypes.object.isRequired,
    rows: PropTypes.array.isRequired,
};

const NOT_CONSIDERED_BUCKET = { color: 'warning', borderColor: 'warning.light' };

// Rows of this version's upload or sync that had a mistake in the row itself.
function NotConsideredSection({ metadata }) {
    const { rows, total, linkedExcel } = notConsideredRows(metadata?.input_validation);
    if (!rows.length) return null;
    return (
        <BucketFrame
            bucket={NOT_CONSIDERED_BUCKET}
            title={`Not considered (${total})`}
            caption={notConsideredText({ linkedExcel, shown: rows.length, total })}
        >
            <SkippedUploadRowsTable rows={rows} />
        </BucketFrame>
    );
}

NotConsideredSection.propTypes = {
    metadata: PropTypes.object.isRequired,
};

function contextChangeRows(review) {
    const listed = (key, change) => (Array.isArray(review?.[key]) ? review[key] : [])
        .map((row) => ({ row, change }));
    return [
        ...listed('watchlist_securities', 'removed'),
        ...listed('removed_listing_securities', 'removed'),
        ...listed('added_securities', 'added'),
        ...listed('country_changed_securities', 'country'),
    ];
}

// The vendors a Settings factor vendor switch moves between. The switch saves
// its vendor only when it publishes, so keeping the current Watchlist keeps both.
function vendorSwitchOf(metadata) {
    const vendorSwitch = metadata?.factor_vendor_switch;
    const to = vendorSwitch?.to?.vendor;
    const from = vendorSwitch?.from?.vendor;
    return to && from
        ? { to: formatUserFactorModel(to), from: formatUserFactorModel(from) }
        : null;
}

function ContextRemovalReview({ review, busy, onConfirm, onKeepCurrent, vendorSwitch = null, detailsControl, diffDetails }) {
    const changes = contextChangeRows(review);
    const impactRows = Array.isArray(review?.portfolio_impact?.securities)
        ? review.portfolio_impact.securities : [];
    const affectedById = new Map(impactRows.map((row) => [
        String(row?.cognive_id || ''),
        (row?.affected_portfolios || []).map((name) => name === 'Current' ? 'Actual' : name),
    ]));
    const impactCount = Number(review?.portfolio_impact?.security_count || 0);
    const actualAffected = impactRows.some((row) => (row.affected_portfolios || [])
        .some((name) => name === 'Current' || name === 'Actual'));
    // The exact reviewed receipt owns the displayed rows. Reuse the normal
    // staged-diff sections, enriching only matching rows with display details.
    const displayDetails = new Map();
    ['removed', 'added'].forEach((change) => {
        const side = change === 'removed' ? 'before' : 'after';
        detailRowsForBucket(diffDetails, change).flatMap((entry) => sideRowsForEntry(entry, side))
            .forEach(({ row }) => displayDetails.set(
                `${change}:${row.cognive_id}:${formatCountry(row)}`, row,
            ));
    });
    const displayRow = (row, change) => {
        const identity = row.display_identity || {};
        const matching = displayDetails.get(`${change}:${row.cognive_id}:${identity.country}`) || {};
        return {
            ...matching,
            cognive_id: row.cognive_id,
            company_name: identity.company_name,
            ticker: identity.ticker,
            inbound_id_type: identity.submitted_identifier_type,
            inbound_id_value: identity.submitted_identifier,
            selected_country: identity.country,
        };
    };
    const grouped = { added: [], removed: [] };
    changes.forEach(({ row, change }) => {
        if (grouped[change]) grouped[change].push(displayRow(row, change));
    });
    const countryChanges = changes.filter(({ change }) => change === 'country').map(({ row }) => ({
        before: { ...displayRow(row, 'country'), selected_country: (row.previous_countries || []).join(', ') },
        after: displayRow(row, 'country'),
        changed_fields: ['selected_country'],
    }));

    return (
        <Box display="flex" flexDirection="column" gap={1.25}>
            <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
                <Button variant="outlined" color="inherit" disabled={busy} onClick={onKeepCurrent}>
                    {vendorSwitch ? `Cancel switch — stay on ${vendorSwitch.from}` : 'Keep current Watchlist'}
                </Button>
                <Button variant="contained" color="primary"
                    disabled={busy || !review?.review_digest || changes.length === 0} onClick={onConfirm}>
                    {busy ? 'Applying changes…' : vendorSwitch
                        ? `Apply changes and switch to ${vendorSwitch.to}` : 'Apply reviewed changes'}
                </Button>
            </Box>
            <Alert severity="info" action={detailsControl}>
                {vendorSwitch
                    ? `Switching from ${vendorSwitch.from} to ${vendorSwitch.to} requires these Watchlist changes.`
                    : 'The selected factor data requires these Watchlist changes.'}
                <Typography variant="body2">
                    {actualAffected
                        ? 'Affected Actual holdings will also be removed. '
                        : impactCount > 0 ? 'Your Position Sizing draft will need review. ' : ''}
                    {vendorSwitch ? `${vendorSwitch.from} remains active until you approve.`
                        : 'Your current Watchlist and factor data remain active until you approve.'}
                </Typography>
            </Alert>
            {['added', 'removed'].map((key) => grouped[key].length > 0 && (
                <ConcreteRowsBucketSection key={key} bucket={DIFF_BUCKETS.find((bucket) => bucket.key === key)}
                    rows={grouped[key]} affectedById={key === 'removed' && impactCount > 0 ? affectedById : undefined} />
            ))}
            {countryChanges.length > 0 && (
                <CountryChangedBucketSection
                    bucket={{ key: 'country_changed', color: 'warning', borderColor: 'warning.light' }}
                    rows={countryChanges} />
            )}

        </Box>
    );
}

ContextRemovalReview.propTypes = {
    review: PropTypes.object,
    busy: PropTypes.bool,
    onConfirm: PropTypes.func.isRequired,
    onKeepCurrent: PropTypes.func.isRequired,
    vendorSwitch: PropTypes.shape({ to: PropTypes.string, from: PropTypes.string }),
    detailsControl: PropTypes.node,
    diffDetails: PropTypes.object,
};

function WatchlistRevisionDetails({
    revision,
    onOpenSnapshot,
    onExportRevision,
    onRestore,
    onApplyContextRefresh,
    onIgnoreContextRefresh,
    onConfirmContextRemovals,
    onKeepCurrentContext,
    contextActionBusy = false,
    contextActionError = '',
    restoreDisabled = false,
    restoreLoading = false,
    exportLoading = false,
}) {
    const counts = getCounts(revision);
    const diffDetails = revision?.diff_details_json || {};
    const retained = countValue(counts, 'retained');
    const populatedBuckets = DIFF_BUCKETS
        .map((bucket) => ({
            bucket,
            rows: detailRowsForBucket(diffDetails, bucket.key),
        }))
        .filter(({ rows }) => rows.length > 0);
    const hasMaterialChange = populatedBuckets.length > 0;
    const description = formatSourceDescription(revision);
    const snapshotAvailable = Boolean(
        revision?.is_current
        || revision?.status === 'applied'
        || revision?.applied_at,
    );
    const restoreAvailable = revision?.restore_available === true;
    const restoreUnavailableReason = revision?.restore_unavailable_reason
        || 'The stored security rows for this version are no longer available.';
    const contextRefresh = revision?.source_metadata_json?.action === 'watchlist_context_refresh';
    const pendingContextRefresh = contextRefresh
        && revision?.status === 'pending_apply'
        && revision?.decision_status === 'pending';
    const contextMetadata = revision?.source_metadata_json || {};
    let revisionLifecycle = null;
    try {
        revisionLifecycle = revision?.lifecycle
            ? normalizeWatchlistLifecycle(revision.lifecycle)
            : null;
    } catch (_error) {
        revisionLifecycle = null;
    }
    const nothingMatched = isNothingMatched(revisionLifecycle);
    const awaitingRemovalReview = contextRefresh
        && revision?.lifecycle?.status === 'running'
        && revision?.lifecycle?.phase === 'removal_review';
    const removalReview = contextMetadata.context_refresh_removal_review;
    // A refresh attempt that never published is not a version to restore.
    const unpublishedContextRefresh = contextRefresh && !isPublishedRevision(revision);
    const contextDetails = <WatchlistContextDetails
        saved={contextMetadata.context_refresh_saved}
        available={contextMetadata.context_refresh_available}
        changedFields={contextMetadata.context_refresh_changed_fields}
        review={awaitingRemovalReview ? removalReview : null}
        vendorSwitch={vendorSwitchOf(contextMetadata)}
        availableLabel={isPublishedRevision(revision) ? 'This version' : 'This update'} />;
    const detailHeading = revisionHeading(revision)
        || formatSourceLabel(
            revisionSource(revision),
            contextMetadata,
            revision?.diff_summary_json?.counts,
        );

    return (
        <Paper
            variant="outlined"
            sx={[{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }, watchlistPanelSx]}
        >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1.5}>
                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                    <Typography variant="subtitle2">
                        {detailHeading === 'Factor data update' ? 'Factor data update details' : `${detailHeading} Details`}
                    </Typography>
                    {description && (
                        <Typography variant="caption" color="text.secondary">
                            {description}
                        </Typography>
                    )}
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    gap={0.75}
                    flexWrap="wrap"
                    sx={{ flex: '2 1 320px' }}
                >
                    <DetailCountChips counts={counts} />
                    {snapshotAvailable && (
                        <Button
                            size="medium"
                            variant="outlined"
                            color="info"
                            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                            onClick={() => onOpenSnapshot(revision)}
                            sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                        >
                            View Snapshot
                        </Button>
                    )}
                    <Button
                        size="medium"
                        variant="outlined"
                        color="primary"
                        startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                        onClick={() => onExportRevision(revision)}
                        disabled={exportLoading}
                        sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                    >
                        {exportLoading ? 'Exporting...' : 'Export this version'}
                    </Button>
                    {onRestore
                        && !pendingContextRefresh
                        && !awaitingRemovalReview
                        && !unpublishedContextRefresh && (
                        <Tooltip
                            title={!restoreAvailable
                                ? restoreUnavailableReason
                                : restoreDisabled
                                    ? 'Finish the staged restore first'
                                    : 'Re-resolve this version through the current security master and stage it for review'}
                        >
                            <span>
                                <Button
                                    size="medium"
                                    variant="outlined"
                                    color="success"
                                    startIcon={<RestoreIcon fontSize="small" />}
                                    onClick={() => onRestore(revision)}
                                    disabled={!restoreAvailable || restoreDisabled || restoreLoading}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 1.5,
                                        minHeight: 32,
                                    }}
                                >
                                    {restoreLoading ? 'Staging...' : 'Restore this Version'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {pendingContextRefresh && (
                        <>
                            <Button
                                size="medium"
                                variant="outlined"
                                onClick={() => onIgnoreContextRefresh(revision)}
                                disabled={contextActionBusy}
                            >
                                Ignore
                            </Button>
                            <Button
                                size="medium"
                                variant="contained"
                                onClick={() => onApplyContextRefresh(revision)}
                                disabled={contextActionBusy}
                            >
                                {contextActionBusy ? 'Starting revalidation...' : 'Run revalidation'}
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={1.5}>
                {contextActionError && <Alert severity="error">{contextActionError}</Alert>}
                {contextMetadata.action === 'market_health_repair' && (
                    <Alert severity="info">
                        Yahoo identified a replacement market-data symbol: {contextMetadata.market_repair_proposal?.repair_symbol}.
                        {' '}Apply rechecks the market data while preserving every security, its selected listing and your current factor data.
                        {' '}Your current Watchlist stays active until that check succeeds.
                    </Alert>
                )}
                <ImportFailureNotice lifecycle={revision?.lifecycle} />
                {nothingMatched && (
                    <Alert severity="info">
                        {nothingMatchedMessage({
                            progress: revisionLifecycle.progress,
                            inputValidation: contextMetadata.input_validation,
                        })}
                    </Alert>
                )}
                <ContextUpdateList revision={revision} />
                {awaitingRemovalReview && removalReview && (
                    <ContextRemovalReview
                        review={removalReview}
                        busy={contextActionBusy}
                        onConfirm={() => onConfirmContextRemovals(revision)}
                        onKeepCurrent={() => onKeepCurrentContext(revision)}
                        vendorSwitch={vendorSwitchOf(contextMetadata)}
                        detailsControl={contextDetails}
                        diffDetails={diffDetails}
                    />
                )}
                {awaitingRemovalReview && !removalReview && (
                    <Alert severity="error">
                        The exact removal preview is unavailable. Publication remains paused.
                    </Alert>
                )}
                {contextRefresh && !awaitingRemovalReview && contextDetails}
                {pendingContextRefresh && (
                    <Alert severity="info">
                        Run revalidation re-checks your current Watchlist against this data. If the
                        securities in your Watchlist stay the same, it publishes right away; otherwise
                        you review the changes first. A Watchlist change you have not applied yet is
                        not affected.
                    </Alert>
                )}
                {!hasMaterialChange && !nothingMatched && !awaitingRemovalReview && (
                    <Typography variant="body2">
                        {contextRefresh
                            ? `${retained || 0} securities retained; no security details changed.`
                            : retained > 0
                            ? `${retained} watchlist rows retained. No changes detected.`
                            : 'No changes detected.'}
                    </Typography>
                )}
                {!awaitingRemovalReview && populatedBuckets.map(({ bucket, rows }) => (
                    <DiffBucketSection
                        key={bucket.key}
                        bucket={bucket}
                        rows={rows}
                    />
                ))}
                <NotConsideredSection metadata={contextMetadata} />
            </Box>
        </Paper>
    );
}

WatchlistRevisionDetails.propTypes = {
    revision: PropTypes.object,
    onOpenSnapshot: PropTypes.func.isRequired,
    onExportRevision: PropTypes.func.isRequired,
    onRestore: PropTypes.func,
    onApplyContextRefresh: PropTypes.func,
    onIgnoreContextRefresh: PropTypes.func,
    onConfirmContextRemovals: PropTypes.func,
    onKeepCurrentContext: PropTypes.func,
    contextActionBusy: PropTypes.bool,
    contextActionError: PropTypes.string,
    restoreDisabled: PropTypes.bool,
    restoreLoading: PropTypes.bool,
    exportLoading: PropTypes.bool,
};

const SNAPSHOT_SORT_DEFAULT = { column: 'identifier', direction: 'asc' };
const SNAPSHOT_ATTRIBUTE_COLUMN_PREFIX = 'attribute:';

function buildSnapshotState() {
    return {
        items: [],
        attributeColumns: [],
        offset: 0,
        total: 0,
        hasMore: false,
        loading: false,
    };
}

// The revision's own attributes, in its display order. Country has its own
// column, so only user attributes are listed, as in the revision's diff rows.
function snapshotAttributeColumns(snapshot) {
    const definitions = Array.isArray(snapshot?.definitions) ? snapshot.definitions : [];
    return definitions
        .filter((definition) => (
            definition?.attribute_definition_id
            && String(definition.source_kind || 'user') === 'user'
        ))
        .sort((left, right) => Number(left.display_order) - Number(right.display_order))
        .map((definition) => ({
            column: `${SNAPSHOT_ATTRIBUTE_COLUMN_PREFIX}${definition.attribute_definition_id}`,
            definitionId: String(definition.attribute_definition_id),
            label: String(definition.display_name || ''),
        }));
}

function snapshotAttributeValue(row, definitionId) {
    return row?.watchlist_attribute_values?.[definitionId];
}

function compareSnapshotText(left, right) {
    return String(left || '').localeCompare(String(right || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}

function snapshotSortValue(row, column) {
    if (column.startsWith(SNAPSHOT_ATTRIBUTE_COLUMN_PREFIX)) {
        return snapshotAttributeValue(row, column.slice(SNAPSHOT_ATTRIBUTE_COLUMN_PREFIX.length)) || '';
    }
    switch (column) {
        case 'identifier': {
            const identifier = formatIdentifier(row);
            return `${identifier.type} ${identifier.value}`.trim();
        }
        case 'ticker':
            return formatTicker(row);
        case 'country':
            return formatCountry(row);
        default:
            return '';
    }
}

function compareSnapshotRows(left, right, sort) {
    const column = sort?.column || SNAPSHOT_SORT_DEFAULT.column;
    const direction = sort?.direction === 'desc' ? 'desc' : 'asc';
    let comparison = 0;
    comparison = compareSnapshotText(
        snapshotSortValue(left, column),
        snapshotSortValue(right, column),
    );
    if (comparison === 0) {
        comparison = compareSnapshotText(
            snapshotSortValue(left, 'identifier'),
            snapshotSortValue(right, 'identifier'),
        );
    }
    return direction === 'desc' ? -comparison : comparison;
}

function SnapshotHeaderCell({
    column,
    label,
    sort,
    onSortChange,
    align = 'left',
    sx = null,
}) {
    const active = sort?.column === column;
    return (
        <TableCell
            align={align}
            sortDirection={active ? sort.direction : false}
            sx={sx}
        >
            <TableSortLabel
                active={active}
                direction={active ? sort.direction : 'asc'}
                onClick={() => onSortChange(column)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );
}

SnapshotHeaderCell.propTypes = {
    column: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    sort: PropTypes.shape({
        column: PropTypes.string,
        direction: PropTypes.oneOf(['asc', 'desc']),
    }).isRequired,
    onSortChange: PropTypes.func.isRequired,
    align: PropTypes.oneOf(['left', 'center', 'right', 'justify', 'inherit']),
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

const SNAPSHOT_LOADING_ROW_COUNT = 8;

// The snapshot's first load: placeholder rows (the theme's row stripes) with
// a Skeleton bar in every cell, hidden from assistive technology (the table
// carries aria-busy).
function SnapshotLoadingRows({ columnCount }) {
    const nextSkeletonWidth = createSkeletonWidths();
    return Array.from({ length: SNAPSHOT_LOADING_ROW_COUNT }, (_, rowIndex) => (
        <TableRow key={rowIndex} aria-hidden="true">
            {Array.from({ length: columnCount }, (__, columnIndex) => (
                <TableCell key={columnIndex}>
                    <Skeleton width={nextSkeletonWidth()} />
                </TableCell>
            ))}
        </TableRow>
    ));
}

SnapshotLoadingRows.propTypes = {
    columnCount: PropTypes.number.isRequired,
};

function SnapshotTable({
    rows,
    attributeColumns,
    loading,
    sort,
    onSortChange,
    onAddBack = null,
    selectedAddBackRowIds = [],
    addBackDisabled = false,
}) {
    const selectedRowIds = new Set(selectedAddBackRowIds.map((rowId) => String(rowId)));
    const showAddBack = Boolean(
        onAddBack && rows.some((row) => row?.add_back_eligible === true),
    );
    const colSpan = 3 + attributeColumns.length + (showAddBack ? 1 : 0);
    return (
        <TableContainer
            sx={[
                {
                    maxHeight: 420,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                },
                watchlistTableFrameSx,
            ]}
        >
            <Table
                size="small"
                stickyHeader
                aria-label="Watchlist snapshot securities"
                aria-busy={loading || undefined}
            >
                <TableHead>
                    <TableRow>
                        <SnapshotHeaderCell
                            column="identifier"
                            label="Identifier"
                            sort={sort}
                            onSortChange={onSortChange}
                        />
                        <SnapshotHeaderCell
                            column="ticker"
                            label="Ticker"
                            sort={sort}
                            onSortChange={onSortChange}
                        />
                        <SnapshotHeaderCell
                            column="country"
                            label="Country"
                            sort={sort}
                            onSortChange={onSortChange}
                        />
                        {attributeColumns.map(({ column, label }) => (
                            <SnapshotHeaderCell
                                key={column}
                                column={column}
                                label={label}
                                sort={sort}
                                onSortChange={onSortChange}
                            />
                        ))}
                        {showAddBack && (
                            <TableCell align="center" sx={{ ...uppercaseLabel(), width: 96 }}>
                                Add back
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => {
                        const isSelected = selectedRowIds.has(String(row?.id || ''));
                        return (
                            <TableRow
                                key={`active-${row?.id || row?.cognive_id || row?.inbound_id_value || index}`}
                                hover
                                selected={isSelected}
                                sx={{
                                    bgcolor: (theme) => {
                                        if (isSelected) {
                                            return alpha(theme.palette.success.main, 0.12);
                                        }
                                        if (index % 2 === 1 && !theme.palette.highContrast) {
                                            return alpha(theme.palette.action.hover, 0.12);
                                        }
                                        return 'transparent';
                                    },
                                    '&.Mui-selected': {
                                        bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                                    },
                                }}
                            >
                                <TableCell>
                                    <IdentifierCell row={row} />
                                </TableCell>
                                <TableCell>{formatTicker(row)}</TableCell>
                                <TableCell>
                                    <CountryDisplay country={formatCountry(row)} />
                                </TableCell>
                                {attributeColumns.map(({ column, definitionId }) => (
                                    <TableCell key={column}>
                                        {formatDisplayValue(snapshotAttributeValue(row, definitionId))}
                                    </TableCell>
                                ))}
                                {showAddBack && (
                                    <TableCell align="center">
                                        {row?.add_back_eligible === true ? (
                                            <Tooltip
                                                title={isSelected
                                                    ? 'Remove this security from the add-back selection'
                                                    : 'Select this security to add back to your watchlist'}
                                            >
                                                <span>
                                                    <Button
                                                        size="small"
                                                        variant={isSelected ? 'outlined' : 'text'}
                                                        color={isSelected ? 'success' : 'primary'}
                                                        startIcon={isSelected
                                                            ? <CheckCircleOutlineIcon fontSize="small" />
                                                            : <AddCircleOutlineIcon fontSize="small" />}
                                                        onClick={() => onAddBack(row)}
                                                        disabled={addBackDisabled || !row?.id}
                                                        sx={{ fontSize: '0.68rem', minWidth: 0 }}
                                                    >
                                                        {isSelected ? 'Selected' : 'Add'}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Typography
                                                component="span"
                                                aria-label="Add back unavailable"
                                                color="text.disabled"
                                            >
                                                -
                                            </Typography>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                    {!loading && rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={colSpan} align="center">
                                No rows match the current filters.
                            </TableCell>
                        </TableRow>
                    )}
                    {loading && rows.length === 0 && <SnapshotLoadingRows columnCount={colSpan} />}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

SnapshotTable.propTypes = {
    rows: PropTypes.array.isRequired,
    attributeColumns: PropTypes.arrayOf(PropTypes.shape({
        column: PropTypes.string.isRequired,
        definitionId: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
    loading: PropTypes.bool.isRequired,
    sort: PropTypes.shape({
        column: PropTypes.string,
        direction: PropTypes.oneOf(['asc', 'desc']),
    }).isRequired,
    onSortChange: PropTypes.func.isRequired,
    onAddBack: PropTypes.func,
    selectedAddBackRowIds: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ),
    addBackDisabled: PropTypes.bool,
};

function normalizeUnassignedWatchlistFields(source) {
    if (!source || typeof source !== 'object') {
        return null;
    }
    const rowCount = Number(source.row_count || 0);
    const fieldCount = Number(source.field_count || 0);
    if (rowCount <= 0 || fieldCount <= 0) {
        return null;
    }
    return {
        row_count: rowCount,
        field_count: fieldCount,
        fields: Array.isArray(source.fields) ? source.fields : [],
    };
}

function normalizeRestorePreview(source) {
    if (!source) return null;
    const preview = source.preview || {};
    const pending = source.pending_revision || null;
    const metadata = pending?.source_metadata_json || source.source_metadata_json || {};
    const unassignedWatchlistFields = normalizeUnassignedWatchlistFields(
        preview.unassigned_watchlist_fields
        || source.unassigned_watchlist_fields
        || metadata.unassigned_watchlist_fields,
    );
    return {
        staged: Boolean(source.staged),
        restoreMode: source.restore_mode || 'replace',
        originDisplayVersion: watchlistDisplayVersion({
            display_version: source.origin_display_version,
        }),
        revisionId: (pending && pending.id) || source.watchlist_revision_id || null,
        revisionSeq: (pending && pending.revision_seq) || source.revision_seq || null,
        restorableCount: Number(preview.restorable_count || 0),
        removedCount: Number(preview.removed_count || 0),
        retainedCount: Number(preview.retained_count || 0),
        alreadyActiveCount: Number(preview.already_active_count || 0),
        casualtyCount: Number(preview.casualty_count || 0),
        casualties: Array.isArray(preview.casualties) ? preview.casualties : [],
        unassignedWatchlistFields,
        diffSummary: source.diff_summary_json || pending?.diff_summary_json || {},
        diffDetails: source.diff_details_json || pending?.diff_details_json || {},
        message: source.message || '',
    };
}

const RESTORE_CASUALTY_REASON_LABELS = {
    security_no_longer_resolvable: 'No longer found',
    selected_country_no_longer_available: 'Country no longer available',
    ambiguous_country_listing: 'Ambiguous listing',
    ticker_unavailable: 'Ticker no longer available',
    missing_inbound_identifier: 'Identifier missing',
    identifier_type_unsupported: 'Identifier type not supported',
};

function restoreCasualtyIdentity(casualty = {}) {
    const type = formatIdentifierType(casualty.identifier_type);
    const value = String(casualty.identifier_value || '').trim();
    const ticker = String(casualty.ticker || '').trim();
    const country = String(casualty.requested_country || '').trim();
    let identity = [type, value].filter(Boolean).join(' ') || ticker || 'Unknown security';
    if (ticker && value && ticker.toUpperCase() !== value.toUpperCase()) {
        identity += ` (${ticker})`;
    }
    return country ? `${identity} · ${country}` : identity;
}

// Securities a version can't bring back are listed by identifier so the user
// knows exactly what the restore will leave out.
function RestoreCasualtyList({ casualties }) {
    const rows = Array.isArray(casualties) ? casualties.filter(Boolean) : [];
    if (!rows.length) return null;
    return (
        <Box sx={{ mb: 1 }} data-testid="restore-casualties">
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {rows.length === 1
                    ? "This security can't be restored"
                    : "These securities can't be restored"}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {rows.map((casualty, index) => (
                    <Typography
                        component="li"
                        variant="body2"
                        key={`${casualty.identifier_type || ''}:${casualty.identifier_value || ''}:${index}`}
                    >
                        {`${restoreCasualtyIdentity(casualty)} — ${
                            RESTORE_CASUALTY_REASON_LABELS[casualty.reason_code]
                            || "Can't be restored"
                        }`}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}

RestoreCasualtyList.propTypes = {
    casualties: PropTypes.arrayOf(PropTypes.object),
};

function RestorePreviewDialog({
    open,
    preview,
    busy,
    error,
    onApply,
    onReject,
    onReviewLater,
}) {
    if (!preview) {
        return null;
    }
    const isAddBack = preview.restoreMode === 'add_back';
    const canApply = preview.staged && Boolean(preview.revisionId);
    const title = isAddBack
        ? 'Add back to watchlist'
        : preview.originDisplayVersion
            ? `Restore Version ${preview.originDisplayVersion}`
            : 'Restore version';
    return (
        <Dialog open={open} onClose={onReviewLater} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <RestoreIcon fontSize="small" color="action" />
                    <Typography variant="subtitle1">{title}</Typography>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && (<Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>)}
                {!preview.staged ? (
                    <Alert severity="info">{preview.message || 'Nothing to restore.'}</Alert>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {preview.restorableCount}
                            {' '}
                            {preview.restorableCount === 1 ? 'security' : 'securities'}
                            {' will be re-resolved through the current security master and '}
                            {isAddBack ? 'added to' : 'restored into'}
                            {' your watchlist.'}
                        </Typography>
                        <Box display="flex" gap={0.75} flexWrap="wrap" sx={{ mb: 1 }}>
                            <Chip size="small" color="success" label={`${preview.restorableCount} restorable`} />
                            {!isAddBack && preview.removedCount > 0 && (
                                <Chip size="small" color="error" label={`${preview.removedCount} removed`} />
                            )}
                            {!isAddBack && preview.retainedCount > 0 && (
                                <Chip size="small" variant="outlined" label={`${preview.retainedCount} unchanged`} />
                            )}
                            {preview.alreadyActiveCount > 0 && (
                                <Chip size="small" variant="outlined" label={`${preview.alreadyActiveCount} already on watchlist`} />
                            )}
                            {preview.casualtyCount > 0 && (
                                <Chip size="small" color="warning" label={`${preview.casualtyCount} can't be restored`} />
                            )}
                            {preview.unassignedWatchlistFields?.field_count > 0 && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={`${preview.unassignedWatchlistFields.field_count} unassigned fields`}
                                />
                            )}
                        </Box>
                        <RestoreCasualtyList casualties={preview.casualties} />
                        {!isAddBack && preview.removedCount > 0 && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                {`Restoring this version removes ${preview.removedCount} `}
                                {preview.removedCount === 1 ? 'security' : 'securities'}
                                {' on your watchlist now but not in this version.'}
                            </Alert>
                        )}
                        <Box sx={{ mt: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Changes compared with your current watchlist
                            </Typography>
                            <ImportDiffDetails
                                diffDetails={preview.diffDetails}
                                diffSummary={preview.diffSummary}
                                mode="watchlist_upload"
                                emptyMessage="No watchlist changes detected."
                            />
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {preview.staged && (
                    <Button color="inherit" onClick={onReject} disabled={busy}>
                        Reject
                    </Button>
                )}
                <Button onClick={onReviewLater} disabled={busy}>Review Later</Button>
                {canApply && (
                    <Button variant="contained" onClick={onApply} disabled={busy}>
                        {busy ? 'Applying...' : 'Apply'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

RestorePreviewDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    preview: PropTypes.object,
    busy: PropTypes.bool,
    error: PropTypes.string,
    onApply: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    onReviewLater: PropTypes.func.isRequired,
};

export default function WatchlistHistoryPanel({
    open,
    onClose,
    initialRevisionRequest = null,
    uploadControl = null,
    activeOperationStatus = null,
    contextStatus = null,
    contextBusy = false,
    onReviewContext = null,
    onUseNewFactorData = null,
    onDeclineContext = null,
}) {
    const { sessionScopeToken } = useSecurityOperationContext();
    const [revisions, setRevisions] = useState([]);
    const [revisionOffset, setRevisionOffset] = useState(0);
    const [hasMoreRevisions, setHasMoreRevisions] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [expandedRevisionId, setExpandedRevisionId] = useState(null);
    const [detailById, setDetailById] = useState({});
    const [detailLoadingById, setDetailLoadingById] = useState({});
    const [detailErrorById, setDetailErrorById] = useState({});
    const [snapshotOpen, setSnapshotOpen] = useState(false);
    const [snapshotRevision, setSnapshotRevision] = useState(null);
    const [snapshotSort, setSnapshotSort] = useState(SNAPSHOT_SORT_DEFAULT);
    const [snapshotState, setSnapshotState] = useState(buildSnapshotState);
    const [snapshotError, setSnapshotError] = useState('');
    const [pendingRestore, setPendingRestore] = useState(null);
    const [restoreConfirmationRevision, setRestoreConfirmationRevision] = useState(null);
    const [restorePreview, setRestorePreview] = useState(null);
    const [restorePreviewOpen, setRestorePreviewOpen] = useState(false);
    const [restoreBusy, setRestoreBusy] = useState(false);
    const [restoreError, setRestoreError] = useState('');
    const [restoreNotice, setRestoreNotice] = useState('');
    const [restoreNoticeAction, setRestoreNoticeAction] = useState(null);
    const [completedResult, setCompletedResult] = useState(null);
    const completedResultContextRef = useRef(null);
    const previousResultContext = completedResultContextRef.current;
    const observedAttempt = activeOperationStatus?.found === true
        && activeOperationStatus?.revisionId && activeOperationStatus?.operationId
        ? { revisionId: activeOperationStatus.revisionId, operationId: activeOperationStatus.operationId,
            revisionSeq: activeOperationStatus.revisionSeq }
        : null;
    const resultScopeChanged = previousResultContext && (
        previousResultContext.open !== open || previousResultContext.sessionScopeToken !== sessionScopeToken
    );
    const resultAttemptChanged = observedAttempt && previousResultContext && (
        previousResultContext.revisionId !== observedAttempt.revisionId
        || previousResultContext.operationId !== observedAttempt.operationId
        || previousResultContext.revisionSeq !== observedAttempt.revisionSeq
    );
    // The progress banner expires independently of the exact result read.
    // Keep its last identified attempt through ordinary status expiry; only
    // a different attempt or panel/session boundary invalidates that read.
    completedResultContextRef.current = {
        ...(!resultScopeChanged ? previousResultContext : null),
        ...observedAttempt,
        open, sessionScopeToken,
        epoch: (previousResultContext?.epoch || 0) + Number(Boolean(resultScopeChanged || resultAttemptChanged)),
    };
    const completedResultEpoch = completedResultContextRef.current.epoch;
    useEffect(() => { setCompletedResult(null); }, [completedResultEpoch]);
    const [restoreStagingId, setRestoreStagingId] = useState(null);
    const [selectedAddBackRowIds, setSelectedAddBackRowIds] = useState([]);
    const [addBackStaging, setAddBackStaging] = useState(false);
    const addBackAttemptRef = useRef(null);
    useEffect(() => { addBackAttemptRef.current = null; }, [open, sessionScopeToken]);
    const [exportingKey, setExportingKey] = useState('');
    const [exportError, setExportError] = useState('');
    const [contextActionBusyId, setContextActionBusyId] = useState('');
    const [contextActionError, setContextActionError] = useState(null);
    const knownRevisionIdsRef = useRef(new Set());
    const revisionHeadersRef = useRef([]);
    const locallyInsertedRevisionIdsRef = useRef(new Set());
    const revisionRefreshesRequestedRef = useRef(new Set());
    const removalReviewRefreshesRequestedRef = useRef(new Set());
    const removalReviewStatusRefreshKeyRef = useRef('');
    const terminalRevisionRefreshesRequestedRef = useRef(new Set());
    const appliedRevisionRefreshesRef = useRef(new Set());
    const appliedRevisionRefreshesInFlightRef = useRef(new Set());
    const revisionListRequestIdRef = useRef(0);
    const handledInitialRevisionRequestRef = useRef('');
    // The exact current pointer from the latest list read. A context update
    // can be current while it is folded into its version as a note.
    const listCurrentRevisionIdRef = useRef('');
    const foldedRevisionIdsRef = useRef(new Set());

    useEffect(() => {
        revisionHeadersRef.current = revisions;
        const foldedRevisionIds = contextUpdateRevisionIds(revisions);
        foldedRevisionIdsRef.current = foldedRevisionIds;
        const knownRevisionIds = new Set([
            ...revisions
                .map((revision) => String(revision?.id || '').trim())
                .filter(Boolean),
            ...foldedRevisionIds,
        ]);
        knownRevisionIdsRef.current = knownRevisionIds;
        knownRevisionIds.forEach((revisionId) => {
            revisionRefreshesRequestedRef.current.delete(revisionId);
        });
    }, [revisions]);

    useEffect(() => {
        if (open) return;
        revisionListRequestIdRef.current += 1;
        handledInitialRevisionRequestRef.current = '';
        setRestoreConfirmationRevision(null);
        locallyInsertedRevisionIdsRef.current.clear();
        revisionRefreshesRequestedRef.current.clear();
        removalReviewRefreshesRequestedRef.current.clear();
        removalReviewStatusRefreshKeyRef.current = '';
        terminalRevisionRefreshesRequestedRef.current.clear();
        appliedRevisionRefreshesRef.current.clear();
        appliedRevisionRefreshesInFlightRef.current.clear();
    }, [open]);

    const latestCurrentRevision = useMemo(
        () => revisions.find((revision) => isCurrentVersion(revision)) || null,
        [revisions],
    );
    const shownRevisions = useMemo(() => withoutReplacedDrafts(revisions), [revisions]);
    const currentRevisionId = latestCurrentRevision?.id == null ? '' : String(latestCurrentRevision.id).trim();
    const isCurrentRevisionLike = useCallback((revisionLike) => {
        const revisionId = revisionLike?.id == null ? '' : String(revisionLike.id).trim();
        return Boolean(revisionId && currentRevisionId && revisionId === currentRevisionId);
    }, [currentRevisionId]);

    const loadRevisions = useCallback(async ({ offset = 0, replace = true } = {}) => {
        const requestId = revisionListRequestIdRef.current + 1;
        revisionListRequestIdRef.current = requestId;
        setListLoading(true);
        setListError('');
        try {
            const resp = await fetchWatchlistRevisions({
                offset,
                limit: REVISION_PAGE_LIMIT,
            });
            if (resp?.success === false) {
                throw new Error(resp?.message || 'Failed to load watchlist history.');
            }
            if (revisionListRequestIdRef.current !== requestId) {
                return;
            }
            const items = Array.isArray(resp?.items) ? resp.items : [];
            if (replace) {
                listCurrentRevisionIdRef.current = String(resp?.current_revision_id || '').trim();
            }
            setRevisions((current) => {
                if (!replace) return [...current, ...items];
                const foldedIds = contextUpdateRevisionIds(items);
                const serverIds = new Set([
                    ...items.map((item) => String(item?.id || '')),
                    ...foldedIds,
                ]);
                items
                    .filter((item) => (
                        item?.is_current === true
                        && String(item?.status || '').trim().toLowerCase() === 'applied'
                    ))
                    .forEach((item) => {
                        locallyInsertedRevisionIdsRef.current.delete(String(item?.id || ''));
                    });
                foldedIds.forEach((revisionId) => {
                    locallyInsertedRevisionIdsRef.current.delete(revisionId);
                });
                const locallyInsertedItems = current.filter((item) => {
                    const revisionId = String(item?.id || '');
                    return revisionId
                        && locallyInsertedRevisionIdsRef.current.has(revisionId)
                        && !serverIds.has(revisionId);
                });
                return [...items, ...locallyInsertedItems].sort((left, right) => (
                    Number(right?.revision_seq || 0) - Number(left?.revision_seq || 0)
                ));
            });
            setRevisionOffset(offset + items.length);
            setHasMoreRevisions(Boolean(resp?.has_more));
            return items;
        } catch (err) {
            if (revisionListRequestIdRef.current === requestId) {
                setListError(err?.message || 'Failed to load watchlist history.');
            }
        } finally {
            if (revisionListRequestIdRef.current === requestId) {
                setListLoading(false);
            }
        }
        return null;
    }, []);

    const refreshAppliedReceiptOnce = useCallback(async (receipt) => {
        const key = `${receipt.revisionSeq}:${receipt.revisionId}`;
        if (appliedRevisionRefreshesRef.current.has(key)) {
            return false;
        }
        if (appliedRevisionRefreshesInFlightRef.current.has(key)) {
            return false;
        }
        appliedRevisionRefreshesInFlightRef.current.add(key);
        try {
            let durableCurrent = false;
            for (let attempt = 0; attempt < 2 && !durableCurrent; attempt += 1) {
                const items = await loadRevisions({ offset: 0, replace: true });
                // A factor-only refresh is folded into its version, so it is
                // never a list entry; the list's exact pointer proves it.
                durableCurrent = Array.isArray(items) && (
                    listCurrentRevisionIdRef.current === receipt.revisionId
                    || items.some((revision) => (
                        String(revision?.id || '').trim() === receipt.revisionId
                        && revision?.is_current === true
                        && String(revision?.status || '').trim().toLowerCase() === 'applied'
                    ))
                );
            }
            if (!durableCurrent) return false;
            appliedRevisionRefreshesRef.current.add(key);
            locallyInsertedRevisionIdsRef.current.delete(receipt.revisionId);
            while (appliedRevisionRefreshesRef.current.size > MAX_APPLIED_REVISION_REFRESH_KEYS) {
                const oldestKey = appliedRevisionRefreshesRef.current.values().next().value;
                appliedRevisionRefreshesRef.current.delete(oldestKey);
            }
            return true;
        } finally {
            appliedRevisionRefreshesInFlightRef.current.delete(key);
        }
    }, [loadRevisions]);

    const refreshAppliedRevisionOnce = useCallback(async (payload) => {
        let lifecycle;
        try {
            lifecycle = normalizeWatchlistLifecycle(payload);
        } catch (_error) {
            return false;
        }
        const classificationResult = parseWatchlistDeltaClassification(payload);
        if (
            !['succeeded', 'unchanged'].includes(lifecycle.status)
            || payload?.ui_commit_complete !== true
            || classificationResult.error
            || classificationResult.classification
                !== WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
        ) {
            return false;
        }
        const receipt = parseWatchlistPublicationReceipt(payload);
        return receipt ? refreshAppliedReceiptOnce(receipt) : false;
    }, [refreshAppliedReceiptOnce]);

    const loadRevisionDetail = useCallback(async (
        revisionLike,
        {
            force = false,
            expand = false,
            showSpinner = true,
        } = {},
    ) => {
        const revisionId = revisionLike?.id == null ? '' : String(revisionLike.id).trim();
        if (!revisionId) return null;
        if (expand) {
            setExpandedRevisionId(revisionId);
        }
        if (!force && detailById[revisionId]) {
            return mergeRevisionDetail(revisionLike, detailById[revisionId]);
        }
        if (showSpinner) {
            setDetailLoadingById((current) => ({ ...current, [revisionId]: true }));
        }
        setDetailErrorById((current) => ({ ...current, [revisionId]: '' }));
        try {
            const resp = await fetchWatchlistRevision(revisionId);
            if (resp?.success === false) {
                throw new Error(resp?.message || 'Failed to load revision details.');
            }
            const detail = revisionDetailFields(resp);
            setDetailById((current) => ({ ...current, [revisionId]: detail }));
            return mergeRevisionDetail(revisionLike, detail);
        } catch (err) {
            setDetailErrorById((current) => ({
                ...current,
                [revisionId]: err?.message || 'Failed to load revision details.',
            }));
            return null;
        } finally {
            setDetailLoadingById((current) => ({ ...current, [revisionId]: false }));
        }
    }, [detailById]);

    const ensureRevisionDetail = useCallback((revisionLike) => (
        loadRevisionDetail(revisionLike, { expand: true })
    ), [loadRevisionDetail]);

    const refreshRevisionFromServer = useCallback(async (revisionId) => {
        const items = await loadRevisions({ offset: 0, replace: true });
        if (!Array.isArray(items)) return null;
        const refreshed = items.find(
            (revision) => String(revision?.id || '').trim() === revisionId,
        );
        if (!refreshed) return null;
        return loadRevisionDetail(refreshed, {
            force: true,
            expand: false,
            showSpinner: false,
        });
    }, [loadRevisionDetail, loadRevisions]);

    const refreshRemovalReviewRevision = useCallback(async (revisionId) => {
        const refreshedDetail = await refreshRevisionFromServer(revisionId);
        return Boolean(
            refreshedDetail?.source_metadata_json?.context_refresh_removal_review
                ?.review_digest,
        );
    }, [refreshRevisionFromServer]);

    const loadPendingRestore = useCallback(async () => {
        try {
            const resp = await fetchLatestWatchlistRevisions();
            const pending = resp?.pending_revision || null;
            if (pending && pending.change_source === 'restore' && pending.id) {
                const detail = await fetchWatchlistRevision(pending.id);
                setPendingRestore(detail || pending);
            } else {
                setPendingRestore(null);
            }
        } catch (err) {
            setPendingRestore(null);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        loadRevisions({ offset: 0, replace: true });
        loadPendingRestore();
    }, [loadPendingRestore, loadRevisions, open]);

    useEffect(() => {
        const revisionId = String(activeOperationStatus?.revisionId || '').trim();
        const awaitingRemovalReview = activeOperationStatus?.found === true
            && activeOperationStatus?.topLevelStatus === 'running'
            && activeOperationStatus?.phase === 'removal_review';
        const refreshKey = [
            revisionId,
            activeOperationStatus?.statusSampleVersion ?? '',
        ].join(':');
        if (!open || !revisionId || !awaitingRemovalReview) {
            if (revisionId) removalReviewRefreshesRequestedRef.current.delete(revisionId);
            removalReviewStatusRefreshKeyRef.current = '';
            return undefined;
        }
        if (removalReviewStatusRefreshKeyRef.current === refreshKey) return undefined;
        removalReviewStatusRefreshKeyRef.current = refreshKey;
        let retryTimer = null;
        const refresh = async (retriesRemaining) => {
            const refreshed = await refreshRemovalReviewRevision(revisionId);
            if (refreshed) return;
            removalReviewStatusRefreshKeyRef.current = '';
            if (retriesRemaining < 1 || typeof window === 'undefined') return;
            retryTimer = window.setTimeout(() => {
                removalReviewStatusRefreshKeyRef.current = refreshKey;
                void refresh(retriesRemaining - 1);
            }, 1000);
        };
        void refresh(1);
        return () => {
            if (retryTimer) window.clearTimeout(retryTimer);
        };
    }, [
        activeOperationStatus?.found,
        activeOperationStatus?.phase,
        activeOperationStatus?.revisionId,
        activeOperationStatus?.statusSampleVersion,
        activeOperationStatus?.topLevelStatus,
        open,
        refreshRemovalReviewRevision,
    ]);

    useEffect(() => {
        const revisionId = String(activeOperationStatus?.revisionId || '').trim();
        const topLevelStatus = String(
            activeOperationStatus?.topLevelStatus || '',
        ).trim().toLowerCase();
        // A failed update, and an import that matched nothing, end without a
        // published version, so the row is reloaded to show how it ended.
        const terminalWithoutPublication = activeOperationStatus?.found === true
            && activeOperationStatus?.phase === 'complete'
            && (
                ['failed', 'partial'].includes(topLevelStatus)
                || activeOperationStatus?.outcome === NOTHING_MATCHED_OUTCOME
            );
        if (!open || !revisionId || !terminalWithoutPublication) return undefined;
        const refreshKey = [
            revisionId,
            activeOperationStatus?.messageCode || topLevelStatus,
        ].join(':');
        if (terminalRevisionRefreshesRequestedRef.current.has(refreshKey)) return undefined;
        terminalRevisionRefreshesRequestedRef.current.add(refreshKey);
        let retryTimer = null;
        const refresh = async (retriesRemaining) => {
            const refreshed = await refreshRevisionFromServer(revisionId);
            if (refreshed?.lifecycle?.phase === 'complete') return;
            terminalRevisionRefreshesRequestedRef.current.delete(refreshKey);
            if (retriesRemaining < 1 || typeof window === 'undefined') return;
            retryTimer = window.setTimeout(() => {
                terminalRevisionRefreshesRequestedRef.current.add(refreshKey);
                void refresh(retriesRemaining - 1);
            }, 1000);
        };
        void refresh(1);
        return () => {
            if (retryTimer) window.clearTimeout(retryTimer);
        };
    }, [
        activeOperationStatus?.found,
        activeOperationStatus?.messageCode,
        activeOperationStatus?.outcome,
        activeOperationStatus?.phase,
        activeOperationStatus?.revisionId,
        activeOperationStatus?.topLevelStatus,
        open,
        refreshRevisionFromServer,
    ]);

    useEffect(() => {
        // A completed add whose candidates all missed admission can keep the
        // current membership and fold into its existing version. It has no
        // replacement-snapshot UI commit to wait for, but History must still
        // replace its optimistic pending entry with the durable result.
        const completedWithoutMembershipChange = [
            WATCHLIST_DELTA_CLASSIFICATIONS.NO_CHANGE,
            WATCHLIST_DELTA_CLASSIFICATIONS.CATEGORIZATION_CHANGED,
            WATCHLIST_DELTA_CLASSIFICATIONS.CUSTOM_METADATA_CHANGED,
        ].includes(activeOperationStatus?.deltaClassification);
        if (
            !open
            || activeOperationStatus?.found !== true
            || (activeOperationStatus?.uiCommitComplete !== true
                && !completedWithoutMembershipChange)
            || String(activeOperationStatus?.topLevelStatus || '').trim().toLowerCase()
                !== 'success'
            || Number(activeOperationStatus?.progressPercent) !== 100
        ) return;
        const receipt = parseWatchlistPublicationReceipt({
            watchlist_revision_id: activeOperationStatus.revisionId,
            revision_seq: activeOperationStatus.revisionSeq,
        });
        if (!receipt) return;
        const completedRevision = revisionHeadersRef.current.find((revision) => (
            String(revision?.id || '').trim() === receipt.revisionId
        ));
        const nothingAddedFromOptimizer = (
            activeOperationStatus.deltaClassification === WATCHLIST_DELTA_CLASSIFICATIONS.NO_CHANGE
            && completedRevision?.source_metadata_json?.action === 'optimizer_saved_portfolio_add_back'
        );
        const operationId = activeOperationStatus.operationId;
        const scopeToken = sessionScopeToken;
        const resultEpoch = completedResultContextRef.current.epoch;
        const stillCurrentAttempt = () => {
            const current = completedResultContextRef.current;
            return current?.open && current.sessionScopeToken === scopeToken
                && current.epoch === resultEpoch
                && current.revisionId === receipt.revisionId && current.operationId === operationId
                && current.revisionSeq === receipt.revisionSeq;
        };
        void refreshAppliedReceiptOnce(receipt).then(async (confirmed) => {
            if (!confirmed || !nothingAddedFromOptimizer) return;
            // Read this exact completed revision, including excluded rows.
            // A folded History row alone cannot supply the failed candidates.
            const detail = await fetchWatchlistRevision(receipt.revisionId);
            const lifecycle = normalizeWatchlistLifecycle(detail);
            if (!stillCurrentAttempt()
                || detail?.id !== receipt.revisionId || detail?.is_current !== true
                || detail?.status !== 'applied' || lifecycle.status !== 'succeeded'
                || lifecycle.watchlist_revision_id !== receipt.revisionId
                || lifecycle.revision_seq !== receipt.revisionSeq
                || !operationId || lifecycle.operation_id !== operationId
                || lifecycle.terminal_receipt?.delta_classification !== 'no_change') return;
            setCompletedResult(detail);
        }).catch(() => {
            if (stillCurrentAttempt()) {
                setListError('The Watchlist update finished, but its result details could not be loaded.');
            }
        });
    }, [
        activeOperationStatus,
        open,
        refreshAppliedReceiptOnce,
        sessionScopeToken,
    ]);

    useEffect(() => {
        if (!open || typeof window === 'undefined') return undefined;
        const handleProgress = (event) => {
            const payload = event?.detail || {};
            if (!isSecurityOperationEventForScope(payload, sessionScopeToken)) return;
            let lifecycle;
            try {
                lifecycle = normalizeWatchlistLifecycle(payload);
            } catch (_error) {
                return;
            }
            const revisionId = String(lifecycle.watchlist_revision_id || '').trim();
            const incomingRevision = payload?.pending_revision || payload?.staged_review || null;
            // Nothing to show until the user picks Keep or Apply; the first
            // event after that choice adds the entry.
            if (awaitsPortfolioDecision(incomingRevision)) return;
            const rawIncomingRevisionId = String(incomingRevision?.id || '').trim();
            if (rawIncomingRevisionId && rawIncomingRevisionId !== revisionId) return;
            // A folded context update is a note on its version, never an entry.
            const incomingRevisionId = foldedRevisionIdsRef.current.has(rawIncomingRevisionId)
                ? ''
                : rawIncomingRevisionId;
            if (incomingRevisionId) {
                locallyInsertedRevisionIdsRef.current.add(incomingRevisionId);
                knownRevisionIdsRef.current.add(incomingRevisionId);
            }
            setRevisions((current) => {
                const withIncoming = incomingRevisionId
                    ? mergeRevisionEventItem(current, {
                        ...incomingRevision,
                        id: incomingRevisionId,
                    })
                    : current;
                return withIncoming.map(
                    (revision) => mergeRevisionProgress(revision, payload),
                );
            });
            const unknownRevision = Boolean(
                revisionId
                && !incomingRevisionId
                && !knownRevisionIdsRef.current.has(revisionId),
            );
            const terminalSucceeded = ['succeeded', 'unchanged'].includes(lifecycle.status);
            if (
                revisionId
                && !(lifecycle.status === 'running' && lifecycle.phase === 'removal_review')
            ) {
                removalReviewRefreshesRequestedRef.current.delete(revisionId);
            }
            if (
                revisionId
                && lifecycle.status === 'running'
                && lifecycle.phase === 'removal_review'
                && !removalReviewRefreshesRequestedRef.current.has(revisionId)
            ) {
                removalReviewRefreshesRequestedRef.current.add(revisionId);
                void refreshRemovalReviewRevision(revisionId).then((refreshed) => {
                    if (!refreshed) {
                        removalReviewRefreshesRequestedRef.current.delete(revisionId);
                    }
                });
            }
            if (
                unknownRevision
                && !terminalSucceeded
                && !revisionRefreshesRequestedRef.current.has(revisionId)
            ) {
                revisionRefreshesRequestedRef.current.add(revisionId);
                loadRevisions({ offset: 0, replace: true });
            }
            void refreshAppliedRevisionOnce(payload);
        };
        // An Ignore elsewhere (the upload review, Build Watchlist additions)
        // closed a pending change: re-read the list so it stops showing as
        // pending. A locally added entry the server no longer lists goes too.
        const handleClosed = (event) => {
            const payload = event?.detail || {};
            if (!isSecurityOperationEventForScope(payload, sessionScopeToken)) return;
            locallyInsertedRevisionIdsRef.current.delete(
                String(payload.watchlist_revision_id || '').trim(),
            );
            loadRevisions({ offset: 0, replace: true });
        };
        window.addEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, handleProgress);
        window.addEventListener(WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT, handleProgress);
        window.addEventListener(WATCHLIST_REVISION_CLOSED_EVENT, handleClosed);
        return () => {
            window.removeEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, handleProgress);
            window.removeEventListener(WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT, handleProgress);
            window.removeEventListener(WATCHLIST_REVISION_CLOSED_EVENT, handleClosed);
        };
    }, [
        loadRevisions,
        open,
        refreshAppliedRevisionOnce,
        refreshRemovalReviewRevision,
        sessionScopeToken,
    ]);

    useEffect(() => {
        if (!open || !initialRevisionRequest?.revisionId) return;
        const requestKey = [
            initialRevisionRequest.requestId ?? '',
            initialRevisionRequest.revisionId,
            initialRevisionRequest.revisionSeq ?? '',
        ].join(':');
        if (handledInitialRevisionRequestRef.current === requestKey) return;
        handledInitialRevisionRequestRef.current = requestKey;
        let cancelled = false;
        const revealRequestedRevision = async () => {
            const items = await loadRevisions({ offset: 0, replace: true });
            if (cancelled) return;
            const requestedRevisionId = String(initialRevisionRequest.revisionId);
            // A factor-only refresh opens the version that holds it as a note.
            let requestedRevision = versionEntryForRevision(items, requestedRevisionId);
            if (!requestedRevision && Array.isArray(items)) {
                const version = await fetchWatchlistRevisionVersion(requestedRevisionId);
                if (cancelled) return;
                requestedRevision = versionEntryForRevision(items, version?.version_revision_id);
            }
            await ensureRevisionDetail(requestedRevision || {
                id: requestedRevisionId,
                revision_seq: initialRevisionRequest.revisionSeq,
            });
        };
        void revealRequestedRevision();
        return () => {
            cancelled = true;
        };
    }, [
        ensureRevisionDetail,
        initialRevisionRequest?.requestId,
        initialRevisionRequest?.revisionId,
        initialRevisionRequest?.revisionSeq,
        loadRevisions,
        open,
    ]);

    const handleToggleExpanded = (revision) => {
        const revisionId = revision?.id;
        if (!revisionId) return;
        if (expandedRevisionId === revisionId) {
            setExpandedRevisionId(null);
            return;
        }
        ensureRevisionDetail(revision);
    };

    const handleApplyContextRefresh = async (revision) => {
        const revisionId = String(revision?.id || '').trim();
        if (!revisionId || contextActionBusyId) return;
        setContextActionBusyId(revisionId);
        setContextActionError(null);
        try {
            const response = await applyWatchlistContextRefreshRevision({
                revisionId,
                attemptId: `watchlist-context-apply:${revisionId}`,
                expectedWatchlistRevisionId: revision.base_revision_id,
            });
            if (response?.success === false || !response) {
                throw new Error(response?.message || 'Could not apply the Watchlist context change.');
            }
            const lifecycle = normalizeWatchlistLifecycle(response);
            if (!['queued', 'running', 'succeeded'].includes(lifecycle.status)
                || !parseWatchlistPublicationReceipt(lifecycle)) {
                throw new Error('The Watchlist context change returned an invalid publication receipt.');
            }
            // The same announcement as every other approval of a factor data
            // update, so it is published like any other Watchlist change.
            announceContextRefreshApproval(lifecycle, sessionScopeToken);
            await loadRevisions({ offset: 0, replace: true });
        } catch (error) {
            setContextActionError({
                revisionId,
                message: error?.message || 'Could not apply the Watchlist context change.',
            });
        } finally {
            setContextActionBusyId('');
        }
    };

    const handleConfirmContextRemovals = async (revision) => {
        const revisionId = String(revision?.id || '').trim();
        const reviewDigest = String(
            revision?.source_metadata_json?.context_refresh_removal_review
                ?.review_digest || '',
        ).trim();
        if (!revisionId || !reviewDigest || contextActionBusyId) return;
        setContextActionBusyId(revisionId);
        setContextActionError(null);
        try {
            const response = await confirmWatchlistContextRemovals({
                revisionId,
                reviewDigest,
                attemptId: `watchlist-context-removal-approve:${revisionId}:${reviewDigest.slice(0, 16)}`,
            });
            if (response?.status === 'review_changed') {
                removalReviewRefreshesRequestedRef.current.delete(revisionId);
                const refreshed = await refreshRemovalReviewRevision(revisionId);
                if (refreshed) {
                    removalReviewRefreshesRequestedRef.current.add(revisionId);
                }
                throw new Error(response?.message || 'The removal impact changed. Review it again.');
            }
            if (response?.success === false || !response) {
                throw new Error(response?.message || 'Could not approve the Watchlist removals.');
            }
            const lifecycle = normalizeWatchlistLifecycle(response);
            const resumed = lifecycle.status === 'running'
                && lifecycle.phase === 'publishing';
            const reviewPaused = lifecycle.status === 'running'
                && lifecycle.phase === 'removal_review';
            const published = ['succeeded', 'unchanged'].includes(lifecycle.status)
                && lifecycle.phase === 'complete';
            if (reviewPaused) {
                // The fast resumed publisher can discover that portfolio
                // membership changed after approval committed. It atomically
                // pauses again with a new digest; replace the cached preview
                // before asking for the next exact decision.
                removalReviewRefreshesRequestedRef.current.delete(revisionId);
                const refreshed = await refreshRemovalReviewRevision(revisionId);
                if (refreshed) {
                    removalReviewRefreshesRequestedRef.current.add(revisionId);
                }
                throw new Error(
                    'The removal impact changed while publication resumed. Review the refreshed exact removals.',
                );
            }
            if (['failed', 'cancelled', 'stale', 'superseded'].includes(lifecycle.status)) {
                throw new Error(
                    `Watchlist publication ${lifecycle.status}. Your current Watchlist was not changed.`,
                );
            }
            if (!resumed && !published) {
                throw new Error('The Watchlist removal approval returned an invalid publication receipt.');
            }
            const detail = scopeSecurityOperationEventDetail({
                ...lifecycle,
                reason: 'watchlist_context_removal_approved',
            }, sessionScopeToken);
            if (detail) {
                window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_EVENT, { detail }));
            }
            await loadRevisions({ offset: 0, replace: true });
        } catch (error) {
            setContextActionError({
                revisionId,
                message: error?.message || 'Could not approve the Watchlist removals.',
            });
        } finally {
            setContextActionBusyId('');
        }
    };

    const handleKeepCurrentContext = async (revision) => {
        const revisionId = String(revision?.id || '').trim();
        const reviewDigest = String(
            revision?.source_metadata_json?.context_refresh_removal_review
                ?.review_digest || '',
        ).trim();
        if (!revisionId || !reviewDigest || contextActionBusyId) return;
        setContextActionBusyId(revisionId);
        setContextActionError(null);
        try {
            const response = await rejectWatchlistContextRemovals({
                revisionId,
                reviewDigest,
                attemptId: `watchlist-context-removal-reject:${revisionId}:${reviewDigest.slice(0, 16)}`,
            });
            if (response?.success === false || !response) {
                throw new Error(response?.message || 'Could not keep the current Watchlist.');
            }
            const lifecycle = normalizeWatchlistLifecycle(response);
            if (lifecycle.status !== 'cancelled') {
                throw new Error('The Watchlist removal rejection returned an invalid receipt.');
            }
            // Kept: this factor data is not run again by itself, but stays
            // available to use later.
            onDeclineContext?.(revision?.source_metadata_json?.context_refresh_available);
            const detail = scopeSecurityOperationEventDetail({
                ...lifecycle,
                reason: 'watchlist_context_removal_rejected',
            }, sessionScopeToken);
            if (detail) {
                window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_EVENT, { detail }));
            }
            await loadRevisions({ offset: 0, replace: true });
        } catch (error) {
            setContextActionError({
                revisionId,
                message: error?.message || 'Could not keep the current Watchlist.',
            });
        } finally {
            setContextActionBusyId('');
        }
    };

    const handleIgnoreContextRefresh = async (revision) => {
        const revisionId = String(revision?.id || '').trim();
        if (!revisionId || contextActionBusyId) return;
        setContextActionBusyId(revisionId);
        setContextActionError(null);
        try {
            const response = await ignoreWatchlistRevision({
                revisionId,
                attemptId: `watchlist-context-ignore:${revisionId}`,
            });
            if (response?.success === false || !response) {
                throw new Error(response?.message || 'Could not ignore the Watchlist context change.');
            }
            await loadRevisions({ offset: 0, replace: true });
        } catch (error) {
            setContextActionError({
                revisionId,
                message: error?.message || 'Could not ignore the Watchlist context change.',
            });
        } finally {
            setContextActionBusyId('');
        }
    };

    const handleExportHistory = useCallback(async () => {
        if (exportingKey) return;
        setExportingKey('history');
        setExportError('');
        const resp = await downloadWatchlistRevisionExport();
        setExportingKey('');
        if (!resp || resp.success === false) {
            setExportError(resp?.message || 'Watchlist history export failed.');
        }
    }, [exportingKey]);

    const handleExportRevision = useCallback(async (revision) => {
        const revisionId = revision?.id == null ? '' : String(revision.id).trim();
        if (!revisionId || exportingKey) return;
        setExportingKey(`revision:${revisionId}`);
        setExportError('');
        const resp = await downloadWatchlistRevisionExport({ revisionId });
        setExportingKey('');
        if (!resp || resp.success === false) {
            setExportError(resp?.message || 'Watchlist revision export failed.');
        }
    }, [exportingKey]);

    const handleStageRestoreResponse = useCallback((resp) => {
        if (showWatchlistDraftGuidance(resp, sessionScopeToken)) {
            setSelectedAddBackRowIds([]);
            setRestorePreviewOpen(false);
            onClose?.();
            return;
        }
        if (!resp || resp.success === false) {
            setRestoreError(resp?.message || resp?.detail?.message || 'Restore could not be staged.');
            setRestorePreview(null);
            setRestorePreviewOpen(true);
            return;
        }
        setRestoreError('');
        setRestorePreview(normalizeRestorePreview(resp));
        setRestorePreviewOpen(true);
        if (resp.staged) {
            loadPendingRestore();
        }
    }, [loadPendingRestore, onClose, sessionScopeToken]);

    const handleRequestFullRestore = useCallback((revision) => {
        if (!revision?.id || restoreStagingId) return;
        setRestoreError('');
        setRestoreConfirmationRevision(revision);
    }, [restoreStagingId]);

    const handleCancelRestoreConfirmation = useCallback(() => {
        if (restoreStagingId) return;
        setRestoreConfirmationRevision(null);
    }, [restoreStagingId]);

    const handleStageFullRestore = useCallback(async () => {
        const revision = restoreConfirmationRevision;
        const revisionId = revision?.id;
        if (!revisionId || restoreStagingId) return;
        setRestoreStagingId(String(revisionId));
        setRestoreNotice('');
        setRestoreNoticeAction(null);
        const resp = await stageWatchlistRestore({ revisionId });
        setRestoreStagingId(null);
        if (resp?.success) setRestoreConfirmationRevision(null);
        handleStageRestoreResponse(resp);
    }, [handleStageRestoreResponse, restoreConfirmationRevision, restoreStagingId]);

    const handleToggleAddBack = useCallback((row) => {
        const rowId = row?.id == null ? '' : String(row.id);
        if (!rowId || row?.add_back_eligible !== true) return;
        addBackAttemptRef.current = null;
        setSelectedAddBackRowIds((current) => (
            current.includes(rowId)
                ? current.filter((selectedId) => selectedId !== rowId)
                : [...current, rowId]
        ));
    }, []);

    const handleStageSelectedAddBack = useCallback(async () => {
        const revisionId = snapshotRevision?.id;
        if (!revisionId || selectedAddBackRowIds.length === 0 || addBackStaging) return;
        setAddBackStaging(true);
        setRestoreNotice('');
        setRestoreNoticeAction(null);
        const attemptKey = JSON.stringify([revisionId, [...selectedAddBackRowIds].sort()]);
        if (addBackAttemptRef.current?.key !== attemptKey) {
            addBackAttemptRef.current = {
                key: attemptKey,
                id: globalThis.crypto?.randomUUID?.()
                    || `add-back:${Date.now()}:${Math.random().toString(16).slice(2)}`,
            };
        }
        const resp = await stageWatchlistRestoreRows({
            revisionId,
            rowIds: selectedAddBackRowIds,
            attemptId: addBackAttemptRef.current.id,
        });
        setAddBackStaging(false);
        if (resp?.success) {
            addBackAttemptRef.current = null;
            setSelectedAddBackRowIds([]);
        }
        handleStageRestoreResponse(resp);
    }, [
        addBackStaging,
        handleStageRestoreResponse,
        selectedAddBackRowIds,
        snapshotRevision,
    ]);

    const handleApplyRestore = useCallback(async () => {
        const revisionId = restorePreview?.revisionId || pendingRestore?.id;
        if (!revisionId) return;
        setRestoreBusy(true);
        setRestoreError('');
        const currentRevId = latestCurrentRevision?.id ? String(latestCurrentRevision.id) : undefined;
        const resp = await applyWatchlistRevision({
            revisionId,
            // The pending restore revision is immutable and unique, so its ID
            // is also a stable idempotency boundary across retries/reopens.
            attemptId: restoreApplyAttemptId(revisionId),
            expectedWatchlistRevisionId: currentRevId,
        });
        if (!resp || resp.success === false) {
            setRestoreBusy(false);
            setRestoreError(resp?.message || resp?.detail?.message || 'Restore could not be applied.');
            return;
        }
        let lifecycle;
        try {
            lifecycle = normalizeWatchlistLifecycle(resp.lifecycle);
        } catch (error) {
            setRestoreBusy(false);
            setRestoreError(`Restore returned an invalid lifecycle: ${error.message}`);
            return;
        }
        const processingPublication = ['queued', 'running'].includes(lifecycle.status);
        const terminalPublication = ['succeeded', 'unchanged'].includes(lifecycle.status);
        if (!processingPublication && !terminalPublication) {
            setRestoreBusy(false);
            setRestoreError('Restore returned an invalid publication status. Retry the same Apply action.');
            return;
        }
        const publicationReceipt = parseWatchlistPublicationReceipt(lifecycle);
        if (!publicationReceipt) {
            setRestoreBusy(false);
            setRestoreError('Restore returned an invalid publication receipt. Retry the same Apply action.');
            return;
        }
        if (terminalPublication) {
            const classificationResult = parseWatchlistDeltaClassification(
                lifecycle.terminal_receipt,
            );
            if (
                classificationResult.error
                || classificationResult.classification
                    !== WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
            ) {
                setRestoreBusy(false);
                setRestoreError(
                    classificationResult.error
                    || 'Restore returned a non-membership mutation.',
                );
                return;
            }
        }
        setRestorePreviewOpen(false);
        setRestorePreview(null);
        setPendingRestore(null);
        setSnapshotOpen(false);
        if (typeof window !== 'undefined') {
            const publicationPayload = terminalPublication
                ? canonicalWatchlistPublicationObservation(lifecycle)
                : lifecycle;
            const detail = scopeSecurityOperationEventDetail({
                    ...publicationPayload,
                    reason: 'watchlist_restore',
                    watchlist_revision_id: publicationReceipt.revisionId,
                    revision_seq: publicationReceipt.revisionSeq,
                    ui_commit_complete: false,
            }, sessionScopeToken);
            if (detail) {
                window.dispatchEvent(new CustomEvent(
                    terminalPublication
                        ? WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT
                        : WATCHLIST_REVISION_PROGRESS_EVENT,
                    { detail },
                ));
            }
        }
        const isAddBack = restorePreview?.restoreMode === 'add_back';
        const materializedCount = Number(
            resp?.materialized_row_count
            || resp?.applied_watchlist_rows
            || resp?.added_watchlist_rows
            || 0,
        );
        const materializedLabel = materializedCount === 1
            ? '1 security'
            : `${materializedCount} securities`;
        const baseNotice = materializedCount > 0
            ? (
                isAddBack
                    ? `Added ${materializedLabel} back to your watchlist.`
                    : `Restored ${materializedLabel} to your watchlist.`
            )
            : (
                isAddBack
                    ? 'Security added back to your watchlist.'
                    : 'Version restored to your watchlist.'
            );
        const unassignedSummary = terminalPublication
            ? normalizeUnassignedWatchlistFields(resp?.unassigned_watchlist_fields)
            : null;
        if (!terminalPublication) {
            setRestoreNotice(
                'Restore started. Your current watchlist will remain visible until the restored version is ready.',
            );
            setRestoreNoticeAction(null);
        } else if (unassignedSummary) {
            const rowLabel = unassignedSummary.row_count === 1
                ? '1 security'
                : `${unassignedSummary.row_count} securities`;
            const fieldLabel = unassignedSummary.field_count === 1
                ? '1 unassigned watchlist field'
                : `${unassignedSummary.field_count} unassigned watchlist fields`;
            const verb = unassignedSummary.row_count === 1 ? 'has' : 'have';
            setRestoreNotice(`${baseNotice} ${rowLabel} ${verb} ${fieldLabel}.`);
            setRestoreNoticeAction('review_unassigned_fields');
        } else {
            setRestoreNotice('');
            setRestoreNoticeAction(null);
        }
        void Promise.allSettled([
            loadRevisions({ offset: 0, replace: true }),
            loadPendingRestore(),
        ]);
        setRestoreBusy(false);
    }, [
        latestCurrentRevision,
        loadPendingRestore,
        loadRevisions,
        pendingRestore,
        restorePreview,
        sessionScopeToken,
    ]);

    const handleRejectRestore = useCallback(async () => {
        const revisionId = restorePreview?.revisionId || pendingRestore?.id;
        if (!revisionId) return;
        setRestoreBusy(true);
        setRestoreError('');
        const resp = await discardWatchlistRevision({ revisionId });
        setRestoreBusy(false);
        if (!resp || resp.success === false) {
            setRestoreError(resp?.message || resp?.detail?.message || 'Could not reject the staged restore.');
            return;
        }
        setRestorePreviewOpen(false);
        setRestorePreview(null);
        setPendingRestore(null);
        loadPendingRestore();
        loadRevisions({ offset: 0, replace: true });
    }, [loadPendingRestore, loadRevisions, pendingRestore, restorePreview]);

    const handleReviewRestoreLater = useCallback(() => {
        if (restoreBusy) return;
        setRestorePreviewOpen(false);
    }, [restoreBusy]);

    const loadSnapshot = useCallback(async ({
        revision,
        offset = 0,
        replace = true,
        limit = SNAPSHOT_PAGE_LIMIT,
    }) => {
        const revisionId = revision?.id == null ? '' : String(revision.id).trim();
        if (!revisionId) return;
        setSnapshotState((current) => ({
            ...current,
            loading: true,
        }));
        setSnapshotError('');
        try {
            const resp = await fetchWatchlistRevisionSnapshot({
                revisionId,
                rowKind: 'active',
                offset,
                limit,
            });
            if (resp?.success === false) {
                throw new Error(resp?.message || 'Failed to load snapshot.');
            }
            const items = Array.isArray(resp?.items) ? resp.items : [];
            setSnapshotState((current) => {
                const nextItems = replace
                    ? items
                    : [...(current.items || []), ...items];
                return {
                    items: nextItems,
                    attributeColumns: replace
                        ? snapshotAttributeColumns(resp?.watchlist_attribute_snapshot)
                        : current.attributeColumns,
                    offset: offset + items.length,
                    total: Number(resp?.total || 0),
                    hasMore: Boolean(resp?.has_more),
                    loading: false,
                };
            });
        } catch (err) {
            setSnapshotError(err?.message || 'Failed to load snapshot.');
            setSnapshotState((current) => ({
                ...current,
                loading: false,
            }));
        }
    }, []);

    const openSnapshotForRevision = (revision) => {
        if (!revision?.id) return;
        setSnapshotRevision(revision);
        setSnapshotSort(SNAPSHOT_SORT_DEFAULT);
        setSnapshotState(buildSnapshotState());
        setSnapshotError('');
        setSelectedAddBackRowIds([]);
        setSnapshotOpen(true);
        loadSnapshot({
            revision,
            offset: 0,
            replace: true,
        });
    };

    const handleSnapshotSortChange = useCallback((column) => {
        setSnapshotSort((current) => {
            if (current.column !== column) {
                return { column, direction: 'asc' };
            }
            return {
                column,
                direction: current.direction === 'asc' ? 'desc' : 'asc',
            };
        });
    }, []);

    const handleLoadMoreSnapshot = () => {
        if (!snapshotRevision || !snapshotState.hasMore || snapshotState.loading) return;
        loadSnapshot({
            revision: snapshotRevision,
            offset: snapshotState.offset || 0,
            replace: false,
        });
    };

    const snapshotRowsForDisplay = useMemo(() => (
        [...(snapshotState.items || [])]
            .sort((left, right) => compareSnapshotRows(left, right, snapshotSort))
    ), [snapshotSort, snapshotState.items]);

    const snapshotLoading = snapshotState.loading;
    const snapshotHasMore = snapshotState.hasMore;
    const snapshotLoadedCount = snapshotRowsForDisplay.length;
    const snapshotTotal = Number(snapshotState.total || 0);
    const snapshotSummaryText = `Showing ${snapshotLoadedCount} loaded of ${snapshotTotal} watchlist rows. Sorting applies to loaded rows.`;

    return (
        <>
            <WatchlistCompletedResultDialog result={completedResult}
                onClose={() => setCompletedResult(null)} />
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: [{
                        alignSelf: 'flex-start',
                        mt: '8vh',
                        height: '76vh',
                        maxHeight: '76vh',
                        width: '68vw',
                        maxWidth: 860,
                    }, watchlistPanelSx],
                }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={1}>
                            <HistoryIcon fontSize="small" color="action" />
                            <Typography variant="subtitle1">Watchlist History</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ ml: 'auto' }}>
                            {uploadControl}
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                                onClick={handleExportHistory}
                                disabled={Boolean(exportingKey)}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                                {exportingKey === 'history' ? 'Exporting...' : 'Export history'}
                            </Button>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ overflowY: 'auto' }}>
                    <Box sx={{ mb: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="subtitle2">
                                {APPLICATION_CONTEXT_LABELS.watchlist_actual_portfolio} context
                            </Typography>
                            {contextStatus?.status === 'update_available' && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={contextStatus.requires_initial_validation
                                        ? 'Validation required'
                                        : 'Update available'}
                                />
                            )}
                            {contextStatus?.status === 'current' && (
                                <Chip size="small" color="success" variant="outlined" label="In sync" />
                            )}
                            {contextStatus?.status === 'update_available'
                                && !contextStatus.requires_initial_validation && (
                                <WatchlistContextDetails saved={contextStatus.saved} available={contextStatus.available}
                                    changedFields={contextStatus.changed_fields} />
                            )}
                            {contextStatus?.status === 'update_available'
                                && !listLoading
                                && !revisions.some((revision) => (
                                    revision?.source_metadata_json?.action === 'watchlist_context_refresh'
                                    && revision?.status === 'pending_apply'
                                ))
                                && (contextStatus.requires_initial_validation
                                    ? onReviewContext
                                    : onUseNewFactorData && hasFactorDataUpdate(contextStatus)) && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    sx={{ ml: 'auto' }}
                                    disabled={contextBusy}
                                    startIcon={contextBusy ? <CircularProgress size={14} color="inherit" /> : null}
                                    onClick={() => (contextStatus.requires_initial_validation
                                        // A Watchlist saved before context tracking is
                                        // checked as a review the user then runs.
                                        ? onReviewContext(contextStatus)
                                        // The same single action as the app-wide prompt:
                                        // it publishes at once when no security changes,
                                        // or opens the update's own review.
                                        : onUseNewFactorData())}
                                >
                                    {contextBusy
                                        ? 'Checking your Watchlist…'
                                        : contextStatus.requires_initial_validation
                                            ? 'Check this Watchlist against current factor data'
                                            : 'Use new factor data'}
                                </Button>
                            )}
                        </Box>
                        {contextStatus?.status === 'update_available'
                            && contextStatus.requires_initial_validation && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                This saved Watchlist predates context tracking. Revalidate it to enable
                                factor and risk data. If validation does not succeed, the published
                                Watchlist remains unchanged.
                            </Alert>
                        )}
                        {contextStatus?.status === 'latest_context_unavailable' && (
                            <Typography variant="body2" color="text.secondary">
                                {contextStatus.requires_initial_validation
                                    ? 'This saved Watchlist still needs initial context validation, '
                                        + 'but the required sources are temporarily unavailable. '
                                        + 'Its published membership remains unchanged.'
                                    : 'The latest context is unavailable. The published Watchlist remains usable.'}
                            </Typography>
                        )}
                        {contextStatus?.status === 'read_error' && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {contextStatus.message
                                    || 'Could not check the latest Watchlist context. The app will retry automatically.'}
                            </Alert>
                        )}
                        {contextStatus?.status === 'authority_inconsistent' && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                Alphamizer cannot verify the published Watchlist context authority.
                                Context refresh is paused until the stored publication lineage is repaired.
                            </Alert>
                        )}
                        {!contextStatus && (
                            <Typography variant="body2" color="text.secondary">
                                Checking Watchlist context…
                            </Typography>
                        )}
                        {contextStatus?.status === 'no_published_watchlist' && (
                            <Typography variant="body2" color="text.secondary">
                                No published Watchlist is available yet.
                            </Typography>
                        )}
                    </Box>
                    {listError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {listError}
                        </Alert>
                    )}
                    {exportError && (
                        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setExportError('')}>
                            {exportError}
                        </Alert>
                    )}
                    {listLoading && shownRevisions.length === 0 && (
                        <Box display="flex" alignItems="center" justifyContent="center" minHeight={140}>
                            <CircularProgress size={24} />
                            <Typography ml={1.5} variant="body2">Loading history...</Typography>
                        </Box>
                    )}
                    {!listLoading && shownRevisions.length === 0 && !listError && (
                        <Typography variant="body2">No watchlist history is available.</Typography>
                    )}
                    {shownRevisions.map((revision, index) => {
                        const revisionId = revision?.id;
                        const expanded = expandedRevisionId === revisionId;
                        const detail = detailById[revisionId]
                            ? mergeRevisionDetail(revision, detailById[revisionId])
                            : revision;
                        const description = formatSourceDescription(revision);
                        const heading = revisionHeading(revision);
                        const currentVersion = isCurrentVersion(revision);
                        return (
                            <Box key={revisionId || `revision-${index}`}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.75}
                                    flexWrap="wrap"
                                    sx={historyLedgerRowSx({
                                        current: currentVersion,
                                        last: index === shownRevisions.length - 1,
                                    })}
                                >
                                    {currentVersion && (
                                        <Tooltip title="Current watchlist" arrow>
                                            <Box
                                                component="span"
                                                aria-label={`Current watchlist, ${revisionAccessibleName(revision)}`}
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    ml: 0.15,
                                                    borderRadius: '50%',
                                                    bgcolor: 'primary.main',
                                                    boxShadow: (theme) => (
                                                        `0 0 7px ${alpha(theme.palette.primary.main, 0.65)}`
                                                    ),
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    <MsiRevisionProgressStatus
                                        revision={revision}
                                        sharedOperationStatus={activeOperationStatus}
                                    />
                                    <SourceLabel revision={revision} />
                                    <Box display="inline-flex" alignItems="baseline" gap={0.65} sx={{ minWidth: 0 }}>
                                        {heading && (
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontSize: '0.76rem', fontWeight: 700, whiteSpace: 'nowrap' }}
                                            >
                                                {heading}
                                            </Typography>
                                        )}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.67rem', whiteSpace: 'nowrap' }}
                                        >
                                            {formatDateTime(revision?.created_at)}
                                        </Typography>
                                    </Box>
                                    <ActorLabel actor={revision?.actor} />
                                    {description && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 260,
                                            }}
                                        >
                                            {description}
                                        </Typography>
                                    )}
                                    <ContextUpdateCaption revision={revision} />
                                    <Box
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                        gap={0.8}
                                        sx={{ ml: 'auto', pl: 0.75 }}
                                    >
                                        <RevisionCountMetrics revision={revision} />
                                        <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleToggleExpanded(revision)}
                                                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${revisionAccessibleName(revision)}`}
                                            >
                                                {expanded ? (
                                                    <ExpandLessIcon fontSize="small" />
                                                ) : (
                                                    <ExpandMoreIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, mb: 0.75 }}>
                                        {detailErrorById[revisionId] && (
                                            <Alert severity="error" sx={{ mb: 1 }}>
                                                {detailErrorById[revisionId]}
                                            </Alert>
                                        )}
                                        {detailLoadingById[revisionId] && !detailById[revisionId] ? (
                                            <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
                                                <CircularProgress size={18} thickness={5} />
                                                <Typography variant="body2">Loading revision details...</Typography>
                                            </Box>
                                        ) : (
                                            <WatchlistRevisionDetails
                                                revision={detail}
                                                onApplyContextRefresh={handleApplyContextRefresh}
                                                onIgnoreContextRefresh={handleIgnoreContextRefresh}
                                                onConfirmContextRemovals={handleConfirmContextRemovals}
                                                onKeepCurrentContext={handleKeepCurrentContext}
                                                contextActionBusy={contextActionBusyId === String(detail?.id || '')}
                                                contextActionError={contextActionError?.revisionId === String(detail?.id || '')
                                                    ? contextActionError.message : ''}
                                                onOpenSnapshot={openSnapshotForRevision}
                                                onExportRevision={handleExportRevision}
                                                onRestore={
                                                    !isCurrentRevisionLike(detail)
                                                        ? handleRequestFullRestore
                                                        : null
                                                }
                                                restoreDisabled={Boolean(pendingRestore)}
                                                restoreLoading={
                                                    String(restoreStagingId || '') === String(detail?.id || '')
                                                }
                                                exportLoading={
                                                    exportingKey === `revision:${String(detail?.id || '')}`
                                                }
                                            />
                                        )}
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })}
                    {hasMoreRevisions && (
                        <Box display="flex" justifyContent="center" mt={1}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => loadRevisions({ offset: revisionOffset, replace: false })}
                                disabled={listLoading}
                            >
                                {listLoading ? 'Loading...' : 'Load More'}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
                <Snackbar
                    open={Boolean(restoreNotice)}
                    autoHideDuration={RESTORE_NOTICE_VISIBLE_MS}
                    onClose={() => {
                        setRestoreNotice('');
                        setRestoreNoticeAction(null);
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        severity="info"
                        variant="filled"
                        onClose={() => {
                            setRestoreNotice('');
                            setRestoreNoticeAction(null);
                        }}
                        action={restoreNoticeAction === 'review_unassigned_fields' ? (
                            <Button
                                color="inherit"
                                size="small"
                                onClick={openSecurityResolutionUnassignedFields}
                                sx={{ textTransform: 'none', fontWeight: 800 }}
                            >
                                Review fields
                            </Button>
                        ) : null}
                    >
                        {restoreNotice}
                    </Alert>
                </Snackbar>
            </Dialog>

            <Dialog
                open={snapshotOpen}
                onClose={() => setSnapshotOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                        <Box>
                            <Typography variant="subtitle1">Watchlist Snapshot</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {`${revisionHeading(snapshotRevision) || 'Watchlist'} - ${formatDateTime(snapshotRevision?.created_at)}`}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {snapshotError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {snapshotError}
                        </Alert>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {snapshotSummaryText} This is the watchlist membership that was actually applied after security resolution.
                    </Typography>
                    <SnapshotTable
                        rows={snapshotRowsForDisplay}
                        attributeColumns={snapshotState.attributeColumns}
                        loading={snapshotLoading}
                        sort={snapshotSort}
                        onSortChange={handleSnapshotSortChange}
                        onAddBack={
                            snapshotRevision && !isCurrentRevisionLike(snapshotRevision)
                                ? handleToggleAddBack
                                : null
                        }
                        selectedAddBackRowIds={selectedAddBackRowIds}
                        addBackDisabled={restoreBusy || addBackStaging}
                    />
                    {snapshotHasMore && (
                        <Box display="flex" justifyContent="center" mt={1}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleLoadMoreSnapshot}
                                disabled={snapshotLoading}
                            >
                                {snapshotLoading ? 'Loading...' : 'Load More'}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedAddBackRowIds.length > 0 && (
                        <>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mr: 'auto', pl: 1 }}
                            >
                                {selectedAddBackRowIds.length}
                                {' selected'}
                            </Typography>
                            <Button
                                color="inherit"
                                onClick={() => setSelectedAddBackRowIds([])}
                                disabled={addBackStaging}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                                onClick={handleStageSelectedAddBack}
                                disabled={restoreBusy || addBackStaging}
                            >
                                {addBackStaging
                                    ? 'Staging...'
                                    : `Review ${selectedAddBackRowIds.length} ${selectedAddBackRowIds.length === 1 ? 'Addition' : 'Additions'}`}
                            </Button>
                        </>
                    )}
                    <Button
                        onClick={() => {
                            setSelectedAddBackRowIds([]);
                            setSnapshotOpen(false);
                        }}
                        disabled={addBackStaging}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {restoreConfirmationRevision && (
                <Dialog
                    open
                    onClose={handleCancelRestoreConfirmation}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>
                        {`Restore ${revisionHeading(restoreConfirmationRevision) || 'this version'}?`}
                    </DialogTitle>
                    <DialogContent dividers>
                        {restoreError && (
                            <Alert severity="error" sx={{ mb: 1.5 }}>
                                {restoreError}
                            </Alert>
                        )}
                        <Typography variant="body2">
                            Stage this historical version to compare it with your current watchlist.
                            Nothing changes until you review the complete diff and click Apply.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCancelRestoreConfirmation}
                            disabled={Boolean(restoreStagingId)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleStageFullRestore}
                            disabled={Boolean(restoreStagingId)}
                        >
                            {restoreStagingId ? 'Staging...' : 'Stage'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <RestorePreviewDialog
                open={restorePreviewOpen}
                preview={restorePreview}
                busy={restoreBusy}
                error={restoreError}
                onApply={handleApplyRestore}
                onReject={handleRejectRestore}
                onReviewLater={handleReviewRestoreLater}
            />
        </>
    );
}

WatchlistHistoryPanel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    initialRevisionRequest: PropTypes.shape({
        requestId: PropTypes.number,
        revisionId: PropTypes.string,
        revisionSeq: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    uploadControl: PropTypes.node,
    activeOperationStatus: PropTypes.object,
    contextStatus: PropTypes.object,
    contextBusy: PropTypes.bool,
    onReviewContext: PropTypes.func,
    onUseNewFactorData: PropTypes.func,
    onDeclineContext: PropTypes.func,
};
